import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import LogoDisplay from './LogoDisplay';

function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [criadores, setCriadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showLogoForm, setShowLogoForm] = useState(false);
  const [showProxyForm, setShowProxyForm] = useState(false);
  const [proxyConfig, setProxyConfig] = useState('');
  const [proxyProvider, setProxyProvider] = useState('dataimpulse'); // 'dataimpulse' | 'proxycheap'
  const [claroServidorUrl, setClaroServidorUrl] = useState('');
  const [loadingProxy, setLoadingProxy] = useState(false);
  const [proxyStatus, setProxyStatus] = useState(null); // null, 'not_configured', 'connected', 'disconnected', 'testing'
  const [testingProxy, setTestingProxy] = useState(false);
  const [savingClaroServidor, setSavingClaroServidor] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'usuario',
    vencimento: '',
    criador_id: '',
    limite_usuarios: '',
    limite_consultas: '',
    limite_consultas_cpf: '',
    limite_consultas_cpf_tem_cnpj: '',
    operadoras_permitidas: [],
    limite_individual: '',
    limite_individual_cpf: '',
    limite_individual_cpf_tem_cnpj: '',
    pode_colocar_vencimento: false,
    pode_consultar_cpf: false,
    pode_consultar_cpf_tem_cnpj: false
  });
  const [limiteInfo, setLimiteInfo] = useState(null);
  const [limiteInfoAtual, setLimiteInfoAtual] = useState(null);
  const [limiteConsultasInfo, setLimiteConsultasInfo] = useState(null);
  const [limiteInfoCpf, setLimiteInfoCpf] = useState(null);
  const [limiteInfoCpfTemCnpj, setLimiteInfoCpfTemCnpj] = useState(null);
  const [usuariosLimitesCpf, setUsuariosLimitesCpf] = useState([]);
  const [usuariosLimitesCpfTemCnpj, setUsuariosLimitesCpfTemCnpj] = useState([]);
  const [operadorasDisponiveis, setOperadorasDisponiveis] = useState([]);
  const [proprietariosLimites, setProprietariosLimites] = useState([]);
  const [usuariosInfo, setUsuariosInfo] = useState([]);
  const [todosLimites, setTodosLimites] = useState([]);
  const [loadingLimites, setLoadingLimites] = useState(false);
  const [proprietarioPodeColocarVencimento, setProprietarioPodeColocarVencimento] = useState(false);
  const [consultasLog, setConsultasLog] = useState([]);
  const [loadingLog, setLoadingLog] = useState(false);

  // Definir essas variáveis antes dos useEffects e funções que as usam
  const isAdmin = currentUser?.role === 'admin';
  const isProprietario = currentUser?.role === 'proprietario';

  useEffect(() => {
    loadUsers();
    loadCriadores();
    loadCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      loadProprietariosLimites();
      loadTodosLimites();
      loadProxyConfig();
      loadConsultasLog();
    } else if (currentUser?.role === 'proprietario') {
      loadUsuariosInfo();
      loadLimitesProprietario();
      // Verificar se proprietário tem permissão para colocar vencimento
      setProprietarioPodeColocarVencimento(currentUser.pode_colocar_vencimento === 1 || currentUser.pode_colocar_vencimento === true);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser?.role === 'proprietario') {
      loadLimiteInfoAtual();
      // Carregar operadoras do proprietário quando estiver criando usuário
      if (formData.role === 'usuario') {
        atualizarOperadorasDisponiveis(currentUser.id);
      }
    } else if (currentUser?.role === 'admin' && formData.role === 'usuario') {
      // Se admin está criando usuário e não selecionou criador, todas as operadoras estão disponíveis
      if (!formData.criador_id) {
        setOperadorasDisponiveis(['vivo', 'claro', 'tim', 'desktop', 'gigamais', 'vero', 'nio', 'brisanet', 'aonet', 'ligga', 'mhnet', 'ampernet', 'zaaz', 'uno']);
      } else {
        atualizarOperadorasDisponiveis(formData.criador_id);
      }
    }
  }, [currentUser, formData.role, formData.criador_id]);

  const loadLimiteInfoAtual = async () => {
    if (currentUser?.role === 'proprietario') {
      try {
        const response = await axios.get(`/users/${currentUser.id}/limite-info`);
        setLimiteInfoAtual(response.data);
      } catch (err) {
        console.error('Erro ao carregar informações de limite:', err);
      }
    }
  };

  const loadLimitesProprietario = async () => {
    if (currentUser?.role !== 'proprietario') return;
    try {
      const [resConsultas, resCpf, resCpfTemCnpj, resUsuariosCpf, resUsuariosCpfTemCnpj] = await Promise.all([
        axios.get(`/users/${currentUser.id}/limite-consultas-info`).catch(() => ({ data: null })),
        axios.get(`/users/${currentUser.id}/limite-info-cpf`).catch(() => ({ data: null })),
        axios.get(`/users/${currentUser.id}/limite-info-cpf-tem-cnpj`).catch(() => ({ data: null })),
        axios.get(`/users/${currentUser.id}/usuarios-limites-cpf`).catch(() => ({ data: [] })),
        axios.get(`/users/${currentUser.id}/usuarios-limites-cpf-tem-cnpj`).catch(() => ({ data: [] }))
      ]);
      setLimiteConsultasInfo(resConsultas.data);
      setLimiteInfoCpf(resCpf.data);
      setLimiteInfoCpfTemCnpj(resCpfTemCnpj.data);
      setUsuariosLimitesCpf(Array.isArray(resUsuariosCpf.data) ? resUsuariosCpf.data : []);
      setUsuariosLimitesCpfTemCnpj(Array.isArray(resUsuariosCpfTemCnpj.data) ? resUsuariosCpfTemCnpj.data : []);
    } catch (err) {
      console.error('Erro ao carregar limites do proprietário:', err);
    }
  };

  const atualizarOperadorasDisponiveis = async (criadorId) => {
    try {
        if (!criadorId || criadorId === '') {
                    // Se não há criador, admin tem todas as operadoras
        if (currentUser?.role === 'admin') {
          setOperadorasDisponiveis(['vivo', 'claro', 'tim', 'desktop', 'gigamais', 'vero', 'nio', 'brisanet', 'aonet', 'ligga', 'mhnet', 'ampernet', 'zaaz', 'uno']);
        } else if (currentUser?.role === 'proprietario') {
          // Proprietário vê apenas suas próprias operadoras
          const operadoras = currentUser.operadoras_permitidas;
          if (operadoras) {
            try {
              const operadorasArray = typeof operadoras === 'string' ? JSON.parse(operadoras) : operadoras;
              setOperadorasDisponiveis(Array.isArray(operadorasArray) ? operadorasArray : []);
            } catch (e) {
              setOperadorasDisponiveis([]);
            }
          } else {
            setOperadorasDisponiveis([]);
          }
        }
        return;
      }

      // Buscar criador para obter suas operadoras usando rota específica
      try {
        const response = await axios.get(`/users/${criadorId}`);
        const criador = response.data;
        
        if (criador) {
          if (criador.role === 'admin') {
            setOperadorasDisponiveis(['vivo', 'claro', 'tim', 'desktop', 'gigamais', 'vero', 'nio', 'brisanet', 'aonet', 'ligga', 'mhnet', 'ampernet', 'zaaz', 'uno']);
          } else if (criador.role === 'proprietario' && criador.operadoras_permitidas) {
            try {
              const operadoras = typeof criador.operadoras_permitidas === 'string' 
                ? JSON.parse(criador.operadoras_permitidas) 
                : criador.operadoras_permitidas;
              setOperadorasDisponiveis(Array.isArray(operadoras) ? operadoras : []);
            } catch (e) {
              setOperadorasDisponiveis([]);
            }
          } else {
            setOperadorasDisponiveis([]);
          }
        } else {
          setOperadorasDisponiveis([]);
        }
      } catch (err) {
        console.error('Erro ao buscar criador:', err);
        setOperadorasDisponiveis([]);
      }
    } catch (err) {
      console.error('Erro ao carregar operadoras disponíveis:', err);
      setOperadorasDisponiveis([]);
    }
  };

  const loadCurrentUser = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
        // Carregar dados completos do usuário atual via rota específica
        try {
          const response = await axios.get(`/users/me`);
          if (response.data) {
            setCurrentUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
          }
        } catch (err) {
          // Se falhar, usar dados do localStorage
          console.warn('Não foi possível atualizar dados do usuário:', err);
        }
      }
    } catch (err) {
      console.error('Erro ao carregar usuário atual:', err);
    }
  };

  const loadProxyConfig = async () => {
    if (!isAdmin) return;
    try {
      setLoadingProxy(true);
      const [proxyRes, providerRes, claroRes] = await Promise.all([
        axios.get('/settings/proxy-melhorplano'),
        axios.get('/settings/proxy-provider').catch(() => ({ data: { proxy_provider: 'dataimpulse' } })),
        axios.get('/settings/claro-servidor-url').catch(() => ({ data: { claro_servidor_url: '' } }))
      ]);
      const config = proxyRes.data.proxy_melhorplano || '';
      setProxyConfig(config);
      setProxyProvider(providerRes.data?.proxy_provider === 'proxycheap' ? 'proxycheap' : 'dataimpulse');
      setClaroServidorUrl(claroRes.data?.claro_servidor_url || '');
      
      // Se não há configuração, definir status como não configurado
      if (!config || config.trim() === '') {
        setProxyStatus('not_configured');
      } else {
        // Testar conexão automaticamente ao carregar
        await testProxyConnection();
      }
    } catch (err) {
      console.error('Erro ao carregar configuração de proxy:', err);
      setError('Erro ao carregar configuração de proxy');
      setProxyStatus('error');
    } finally {
      setLoadingProxy(false);
    }
  };

  const loadConsultasLog = async () => {
    if (!isAdmin) return;
    try {
      setLoadingLog(true);
      const response = await axios.get('/admin/consultas/log');
      setConsultasLog(response.data.consultas || []);
    } catch (err) {
      console.error('Erro ao carregar log de consultas:', err);
      setError('Erro ao carregar log de consultas');
    } finally {
      setLoadingLog(false);
    }
  };

  const testProxyConnection = async () => {
    if (!isAdmin) return;
    try {
      setTestingProxy(true);
      setProxyStatus('testing');
      const response = await axios.post('/settings/proxy-melhorplano/test');
      setProxyStatus(response.data.status || (response.data.connected ? 'connected' : 'disconnected'));
    } catch (err) {
      console.error('Erro ao testar proxy:', err);
      setProxyStatus('error');
      setError(err.response?.data?.message || 'Erro ao testar conectividade do proxy');
    } finally {
      setTestingProxy(false);
    }
  };

  const handleProxyProviderChange = async (provider) => {
    setError('');
    setSuccess('');
    try {
      await axios.put('/settings/proxy-provider', { proxy_provider: provider });
      setProxyProvider(provider);
      setSuccess('Provedor de proxy atualizado para ' + (provider === 'proxycheap' ? 'ProxyCheap' : 'DataImpulse') + '!');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao atualizar provedor de proxy');
    }
  };

  const handleProxySubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await Promise.all([
        axios.put('/settings/proxy-melhorplano', { proxy_melhorplano: proxyConfig }),
        axios.put('/settings/proxy-provider', { proxy_provider: proxyProvider })
      ]);
      setSuccess('Configuração de proxy salva com sucesso!');
      
      // Atualizar status
      if (!proxyConfig || proxyConfig.trim() === '') {
        setProxyStatus('not_configured');
      } else {
        // Testar conexão após salvar
        await testProxyConnection();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao salvar configuração de proxy');
      setProxyStatus('error');
    }
  };

  const handleClaroServidorSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      setSavingClaroServidor(true);
      await axios.put('/settings/claro-servidor-url', {
        claro_servidor_url: claroServidorUrl.trim() || ''
      });
      setSuccess('URL do Servidor Claro salva com sucesso!');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao salvar URL do Servidor Claro');
    } finally {
      setSavingClaroServidor(false);
    }
  };

  const loadCriadores = async () => {
    try {
      const response = await axios.get('/users/criadores');
      setCriadores(response.data);
    } catch (err) {
      console.error('Erro ao carregar criadores:', err);
    }
  };

  const loadProprietariosLimites = async () => {
    try {
      setLoadingLimites(true);
      const response = await axios.get('/users/proprietarios-limites');
      setProprietariosLimites(response.data);
    } catch (err) {
      console.error('Erro ao carregar limites de proprietários:', err);
      setError(err.response?.data?.error || 'Erro ao carregar limites de proprietários');
    } finally {
      setLoadingLimites(false);
    }
  };

  const loadTodosLimites = async () => {
    try {
      setLoadingLimites(true);
      const response = await axios.get('/users/todos-limites');
      setTodosLimites(response.data);
    } catch (err) {
      console.error('Erro ao carregar todos os limites:', err);
      setError(err.response?.data?.error || 'Erro ao carregar todos os limites');
    } finally {
      setLoadingLimites(false);
    }
  };

  const loadUsuariosInfo = async () => {
    const userId = currentUser?.id;
    if (!userId) return;
    try {
      setLoadingLimites(true);
      const response = await axios.get(`/users/${userId}/usuarios-info`);
      setUsuariosInfo(response.data);
    } catch (err) {
      console.error('Erro ao carregar informações de usuários:', err);
      // Não mostrar erro global, apenas log
      if (err.response?.status !== 403) {
        setError(err.response?.data?.error || 'Erro ao carregar informações de usuários');
      }
    } finally {
      setLoadingLimites(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/users');
      setUsers(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const dataToSend = { ...formData };
      
      // Se for proprietário, vencimento é obrigatório
      if (dataToSend.role === 'proprietario' && !dataToSend.vencimento) {
        setError('Vencimento é obrigatório para proprietários');
        return;
      }

      // Validar limite_usuarios se for proprietário
      if (dataToSend.role === 'proprietario' && dataToSend.limite_usuarios) {
        const limite = parseInt(dataToSend.limite_usuarios);
        if (isNaN(limite) || limite < 1) {
          setError('Limite de usuários deve ser um número positivo');
          return;
        }
        dataToSend.limite_usuarios = limite;
      }

      // Se for usuário, processar vencimento apenas se proprietário tiver permissão
      if (dataToSend.role === 'usuario') {
        // Se proprietário não tem permissão, forçar vencimento null
        if (currentUser?.role === 'proprietario' && !proprietarioPodeColocarVencimento) {
          dataToSend.vencimento = null;
        }
        // Se não especificou vencimento ou não tem permissão, definir como null
        if (!dataToSend.vencimento || (currentUser?.role === 'proprietario' && !proprietarioPodeColocarVencimento)) {
          dataToSend.vencimento = null;
        }
        dataToSend.limite_usuarios = null;
        dataToSend.limite_consultas = null;
        // Se não especificou criador, será definido automaticamente pelo backend
        if (!dataToSend.criador_id) {
          delete dataToSend.criador_id;
        }
        // Se operadoras_permitidas está vazio, passar array vazio (não herdará do criador)
        if (!dataToSend.operadoras_permitidas || dataToSend.operadoras_permitidas.length === 0) {
          dataToSend.operadoras_permitidas = [];
        }

        // Se o usuário logado é proprietário, verificar limite antes de criar
        if (currentUser?.role === 'proprietario') {
          try {
            const limiteResponse = await axios.get(`/users/${currentUser.id}/limite-info`);
            const limiteInfo = limiteResponse.data;
            if (!limiteInfo.podeCriar && limiteInfo.limite !== null) {
              setError(`Limite de usuários atingido. Você já criou ${limiteInfo.criados} de ${limiteInfo.limite} usuários permitidos.`);
              return;
            }
          } catch (err) {
            // Se não conseguir verificar, continuar (backend vai validar)
            console.warn('Não foi possível verificar limite no frontend:', err);
          }
        }
      } else {
        // Para proprietários, remover criador_id
        delete dataToSend.criador_id;
        // Converter operadoras para array se necessário
        if (dataToSend.operadoras_permitidas && !Array.isArray(dataToSend.operadoras_permitidas)) {
          dataToSend.operadoras_permitidas = [];
        }
        // Apenas admin pode definir pode_colocar_vencimento
        if (currentUser?.role !== 'admin') {
          delete dataToSend.pode_colocar_vencimento;
        }
      }

      // Se não for proprietário, remover pode_colocar_vencimento
      if (dataToSend.role !== 'proprietario') {
        delete dataToSend.pode_colocar_vencimento;
      }

      // Separar dados de CPF antes de criar usuário
      const limiteCpfProprietario = dataToSend.limite_consultas_cpf;
      const limiteCpfTemCnpjProprietario = dataToSend.limite_consultas_cpf_tem_cnpj;
      const limiteIndividualCpf = dataToSend.limite_individual_cpf;
      const limiteIndividualCpfTemCnpj = dataToSend.limite_individual_cpf_tem_cnpj;
      const podeConsultarCpf = dataToSend.pode_consultar_cpf;
      const podeConsultarCpfTemCnpj = dataToSend.pode_consultar_cpf_tem_cnpj;
      delete dataToSend.limite_consultas_cpf;
      delete dataToSend.limite_consultas_cpf_tem_cnpj;
      delete dataToSend.limite_individual_cpf;
      delete dataToSend.limite_individual_cpf_tem_cnpj;
      delete dataToSend.pode_consultar_cpf;
      delete dataToSend.pode_consultar_cpf_tem_cnpj;
      
      const response = await axios.post('/users', dataToSend);
      const novoUsuarioId = response.data.id || response.data.user?.id;
      
      // Se criou usuário, configurar limites e permissões de CPF
      if (novoUsuarioId) {
        // Se for proprietário, definir limite de CPF
        if (dataToSend.role === 'proprietario' && isAdmin && limiteCpfProprietario !== '' && limiteCpfProprietario !== null && limiteCpfProprietario !== undefined) {
          try {
            const limiteCpfValue = parseInt(limiteCpfProprietario);
            if (!isNaN(limiteCpfValue)) {
              await axios.put(`/users/${novoUsuarioId}/limite-consultas-cpf`, {
                limite_consultas: limiteCpfValue
              });
            }
          } catch (err) {
            console.error('Erro ao definir limite de CPF do proprietário:', err);
          }
        }
        
        // Se for proprietário, definir limite de CPF TEM CNPJ
        if (dataToSend.role === 'proprietario' && isAdmin && limiteCpfTemCnpjProprietario !== '' && limiteCpfTemCnpjProprietario !== null && limiteCpfTemCnpjProprietario !== undefined) {
          try {
            const limiteCpfTemCnpjValue = parseInt(limiteCpfTemCnpjProprietario);
            if (!isNaN(limiteCpfTemCnpjValue)) {
              await axios.put(`/users/${novoUsuarioId}/limite-consultas-cpf-tem-cnpj`, {
                limite_consultas: limiteCpfTemCnpjValue
              });
            }
          } catch (err) {
            console.error('Erro ao definir limite de CPF TEM CNPJ do proprietário:', err);
          }
        }
        
        // Se for usuário, definir limite individual de CPF
        if (dataToSend.role === 'usuario' && limiteIndividualCpf !== '' && limiteIndividualCpf !== null && limiteIndividualCpf !== undefined) {
          try {
            const proprietarioId = dataToSend.criador_id || (currentUser?.role === 'proprietario' ? currentUser.id : null);
            if (proprietarioId) {
              const limiteCpfValue = parseInt(limiteIndividualCpf);
              if (!isNaN(limiteCpfValue)) {
                await axios.put(`/users/${proprietarioId}/usuarios/${novoUsuarioId}/limite-consultas-cpf`, {
                  limite_consultas: limiteCpfValue
                });
              }
            }
          } catch (err) {
            console.error('Erro ao definir limite individual de CPF:', err);
          }
        }
        
        // Definir permissão de CPF (admin ou proprietário criando seus próprios usuários)
        if ((isAdmin || (isProprietario && dataToSend.role === 'usuario')) && (podeConsultarCpf === true || podeConsultarCpf === 1)) {
          try {
            await axios.put(`/users/${novoUsuarioId}/permissao-cpf`, {
              pode_consultar_cpf: true
            });
          } catch (err) {
            console.error('Erro ao definir permissão de CPF:', err);
          }
        } else if ((isAdmin || (isProprietario && dataToSend.role === 'usuario')) && (podeConsultarCpf === false || podeConsultarCpf === 0)) {
          // Remover permissão se explicitamente desmarcado
          try {
            await axios.put(`/users/${novoUsuarioId}/permissao-cpf`, {
              pode_consultar_cpf: false
            });
          } catch (err) {
            console.error('Erro ao remover permissão de CPF:', err);
          }
        }
        
        // Se for usuário, definir limite individual de CPF TEM CNPJ
        if (dataToSend.role === 'usuario' && limiteIndividualCpfTemCnpj !== '' && limiteIndividualCpfTemCnpj !== null && limiteIndividualCpfTemCnpj !== undefined) {
          try {
            const proprietarioId = dataToSend.criador_id || (currentUser?.role === 'proprietario' ? currentUser.id : null);
            if (proprietarioId) {
              const limiteCpfTemCnpjValue = parseInt(limiteIndividualCpfTemCnpj);
              if (!isNaN(limiteCpfTemCnpjValue)) {
                await axios.put(`/users/${proprietarioId}/usuarios/${novoUsuarioId}/limite-consultas-cpf-tem-cnpj`, {
                  limite_consultas: limiteCpfTemCnpjValue
                });
              }
            }
          } catch (err) {
            console.error('Erro ao definir limite individual de CPF TEM CNPJ:', err);
          }
        }
        
        // Definir permissão de CPF TEM CNPJ (admin ou proprietário criando seus próprios usuários)
        if ((isAdmin || (isProprietario && dataToSend.role === 'usuario')) && (podeConsultarCpfTemCnpj === true || podeConsultarCpfTemCnpj === 1)) {
          try {
            await axios.put(`/users/${novoUsuarioId}/permissao-cpf-tem-cnpj`, {
              pode_consultar_cpf_tem_cnpj: true
            });
          } catch (err) {
            console.error('Erro ao definir permissão de CPF TEM CNPJ:', err);
          }
        } else if ((isAdmin || (isProprietario && dataToSend.role === 'usuario')) && (podeConsultarCpfTemCnpj === false || podeConsultarCpfTemCnpj === 0)) {
          // Remover permissão se explicitamente desmarcado
          try {
            await axios.put(`/users/${novoUsuarioId}/permissao-cpf-tem-cnpj`, {
              pode_consultar_cpf_tem_cnpj: false
            });
          } catch (err) {
            console.error('Erro ao remover permissão de CPF TEM CNPJ:', err);
          }
        }
      }
      
      setSuccess('Usuário criado com sucesso!');
      setFormData({ username: '', password: '', role: 'usuario', vencimento: '', criador_id: '', limite_usuarios: '', limite_consultas: '', limite_consultas_cpf: '', operadoras_permitidas: [], limite_individual: '', limite_individual_cpf: '', limite_individual_cpf_tem_cnpj: '', pode_colocar_vencimento: false, pode_consultar_cpf: false, pode_consultar_cpf_tem_cnpj: false });
      setShowForm(false);
      loadUsers();
      loadCriadores();
      loadLimiteInfoAtual(); // Recarregar informações de limite
      // Recarregar visualizações de limites
      if (currentUser?.role === 'admin') {
        loadProprietariosLimites();
        loadTodosLimites();
      } else if (currentUser?.role === 'proprietario') {
        loadUsuariosInfo();
        loadLimitesProprietario();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao criar usuário');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres');
      return;
    }

    try {
      const userStr = localStorage.getItem('user');
      const user = JSON.parse(userStr);
      await axios.put(`/users/${user.id}/password`, {
        newPassword: passwordData.newPassword
      });
      setSuccess('Senha alterada com sucesso!');
      setPasswordData({ newPassword: '', confirmPassword: '' });
      setShowPasswordForm(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao alterar senha');
    }
  };

  const handleLogoUpload = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!logoFile) {
      setError('Selecione um arquivo de imagem');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('logo', logoFile);

      // Fazer upload do logo
      const uploadResponse = await axios.post('/upload/logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const logoPath = uploadResponse.data.logo_path;

      // Atualizar logo do usuário atual
      const userStr = localStorage.getItem('user');
      const user = JSON.parse(userStr);
      await axios.put(`/users/${user.id}/logo`, { logo_path: logoPath });

      // Atualizar localStorage
      user.logo_path = logoPath;
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('logo_path', logoPath);

      setSuccess('Logo enviado com sucesso!');
      setLogoFile(null);
      setShowLogoForm(false);
      loadUsers();
      window.location.reload(); // Recarregar para atualizar logo no header
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao fazer upload do logo');
    }
  };

  const handleEdit = async (user) => {
    setEditingUser(user);
    
    // Parse operadoras_permitidas se for string JSON
    let operadoras = [];
    if (user.operadoras_permitidas) {
      try {
        operadoras = typeof user.operadoras_permitidas === 'string' 
          ? JSON.parse(user.operadoras_permitidas) 
          : user.operadoras_permitidas;
        if (!Array.isArray(operadoras)) operadoras = [];
      } catch (e) {
        operadoras = [];
      }
    }
    
    // Se for usuário, carregar limite individual e operadoras disponíveis
    let limiteIndividual = null;
    let limiteIndividualCpf = null;
    let limiteIndividualCpfTemCnpj = null;
    let podeConsultarCpf = false;
    let podeConsultarCpfTemCnpj = false;
    let limiteCpfTemCnpjProprietario = null; // Inicializar antes do bloco condicional
    
    if (user.role === 'usuario' && user.criador_id) {
      // Se proprietário está editando seu próprio usuário, buscar limite individual
      if (currentUser?.role === 'proprietario' && user.criador_id === currentUser.id) {
        try {
          const response = await axios.get(`/users/${currentUser.id}/usuarios-limites`);
          const limiteInfo = response.data.find(l => l.usuario_id === user.id);
          limiteIndividual = limiteInfo ? limiteInfo.limite_consultas : null;
        } catch (err) {
          console.error('Erro ao carregar limite individual:', err);
          limiteIndividual = null;
        }
        
        // Carregar limite individual de CPF
        try {
          const responseCpf = await axios.get(`/users/${currentUser.id}/usuarios-limites-cpf`);
          const limiteInfoCpf = responseCpf.data.find(l => l.usuario_id === user.id);
          limiteIndividualCpf = limiteInfoCpf ? limiteInfoCpf.limite_consultas : null;
        } catch (err) {
          console.error('Erro ao carregar limite individual de CPF:', err);
          limiteIndividualCpf = null;
        }
        
        // Verificar permissão de CPF
        try {
          const responsePermissao = await axios.get(`/users/${user.id}`);
          podeConsultarCpf = responsePermissao.data.pode_consultar_cpf || false;
        } catch (err) {
          console.error('Erro ao verificar permissão de CPF:', err);
          podeConsultarCpf = false;
        }
        
        // Carregar limite individual de CPF TEM CNPJ
        try {
          const responseCpfTemCnpj = await axios.get(`/users/${currentUser.id}/usuarios-limites-cpf-tem-cnpj`);
          const limiteInfoCpfTemCnpj = responseCpfTemCnpj.data.find(l => l.usuario_id === user.id);
          limiteIndividualCpfTemCnpj = limiteInfoCpfTemCnpj ? limiteInfoCpfTemCnpj.limite_consultas : null;
        } catch (err) {
          console.error('Erro ao carregar limite individual de CPF TEM CNPJ:', err);
          limiteIndividualCpfTemCnpj = null;
        }
        
        // Verificar permissão de CPF TEM CNPJ
        try {
          const responsePermissao = await axios.get(`/users/${user.id}`);
          podeConsultarCpfTemCnpj = responsePermissao.data.pode_consultar_cpf_tem_cnpj || false;
        } catch (err) {
          console.error('Erro ao verificar permissão de CPF TEM CNPJ:', err);
          podeConsultarCpfTemCnpj = false;
        }
        
        // Carregar operadoras disponíveis do proprietário
        const operadorasProprietario = currentUser.operadoras_permitidas;
        if (operadorasProprietario) {
          try {
            const operadorasArray = typeof operadorasProprietario === 'string' 
              ? JSON.parse(operadorasProprietario) 
              : operadorasProprietario;
            setOperadorasDisponiveis(Array.isArray(operadorasArray) ? operadorasArray : []);
          } catch (e) {
            setOperadorasDisponiveis([]);
          }
        } else {
          setOperadorasDisponiveis([]);
        }
      } else if (currentUser?.role === 'admin') {
        // Admin editando: buscar limite individual e operadoras
        try {
          if (user.criador_id) {
            const response = await axios.get(`/users/${user.criador_id}/usuarios-limites`);
            const limiteInfo = response.data.find(l => l.usuario_id === user.id);
            limiteIndividual = limiteInfo ? limiteInfo.limite_consultas : null;
            
            // Carregar limite individual de CPF
            try {
              const responseCpf = await axios.get(`/users/${user.criador_id}/usuarios-limites-cpf`);
              const limiteInfoCpf = responseCpf.data.find(l => l.usuario_id === user.id);
              limiteIndividualCpf = limiteInfoCpf ? limiteInfoCpf.limite_consultas : null;
            } catch (err) {
              console.error('Erro ao carregar limite individual de CPF:', err);
              limiteIndividualCpf = null;
            }
            
            // Verificar permissão de CPF
            try {
              const responsePermissao = await axios.get(`/users/${user.id}`);
              podeConsultarCpf = responsePermissao.data.pode_consultar_cpf || false;
            } catch (err) {
              console.error('Erro ao verificar permissão de CPF:', err);
              podeConsultarCpf = false;
            }
            
            // Carregar limite individual de CPF TEM CNPJ
            try {
              const responseCpfTemCnpj = await axios.get(`/users/${user.criador_id}/usuarios-limites-cpf-tem-cnpj`);
              const limiteInfoCpfTemCnpj = responseCpfTemCnpj.data.find(l => l.usuario_id === user.id);
              limiteIndividualCpfTemCnpj = limiteInfoCpfTemCnpj ? limiteInfoCpfTemCnpj.limite_consultas : null;
            } catch (err) {
              console.error('Erro ao carregar limite individual de CPF TEM CNPJ:', err);
              limiteIndividualCpfTemCnpj = null;
            }
            
            // Verificar permissão de CPF TEM CNPJ
            try {
              const responsePermissao = await axios.get(`/users/${user.id}`);
              podeConsultarCpfTemCnpj = responsePermissao.data.pode_consultar_cpf_tem_cnpj || false;
            } catch (err) {
              console.error('Erro ao verificar permissão de CPF TEM CNPJ:', err);
              podeConsultarCpfTemCnpj = false;
            }
            
            await atualizarOperadorasDisponiveis(user.criador_id);
          }
        } catch (err) {
          console.error('Erro ao carregar limite individual:', err);
          limiteIndividual = null;
          limiteIndividualCpf = null;
          await atualizarOperadorasDisponiveis(user.criador_id);
        }
      }
    } else {
      setOperadorasDisponiveis(['vivo', 'claro', 'tim', 'desktop', 'gigamais', 'vero', 'nio', 'brisanet', 'aonet', 'ligga', 'mhnet', 'ampernet', 'zaaz', 'uno']);
      
      // Se for proprietário, carregar limite de CPF e permissão
      if (user.role === 'proprietario') {
        try {
          const responseCpf = await axios.get(`/users/${user.id}/limite-info-cpf`);
          limiteIndividualCpf = responseCpf.data.limite || null;
        } catch (err) {
          console.error('Erro ao carregar limite de CPF do proprietário:', err);
          limiteIndividualCpf = null;
        }
        
        // Carregar limite de CPF TEM CNPJ do proprietário
        try {
          const responseCpfTemCnpj = await axios.get(`/users/${user.id}/limite-info-cpf-tem-cnpj`);
          limiteCpfTemCnpjProprietario = responseCpfTemCnpj.data.limite || null;
        } catch (err) {
          console.error('Erro ao carregar limite de CPF TEM CNPJ do proprietário:', err);
          limiteCpfTemCnpjProprietario = null;
        }
        
        // Verificar permissão de CPF do proprietário
        try {
          const responsePermissao = await axios.get(`/users/${user.id}`);
          podeConsultarCpf = responsePermissao.data.pode_consultar_cpf || false;
          podeConsultarCpfTemCnpj = responsePermissao.data.pode_consultar_cpf_tem_cnpj || false;
        } catch (err) {
          console.error('Erro ao verificar permissão de CPF do proprietário:', err);
          podeConsultarCpf = false;
          podeConsultarCpfTemCnpj = false;
        }
      }
    }
    
    setFormData({
      username: user.username,
      password: '', // Não preencher senha na edição
      role: user.role,
      vencimento: user.vencimento ? user.vencimento.split('T')[0] : '',
      criador_id: user.criador_id || '',
      limite_usuarios: user.limite_usuarios || '',
      limite_consultas: user.limite_consultas || '',
      limite_consultas_cpf: limiteIndividualCpf !== null && user.role === 'proprietario' ? limiteIndividualCpf : '',
      limite_consultas_cpf_tem_cnpj: user.role === 'proprietario' && limiteCpfTemCnpjProprietario !== null ? limiteCpfTemCnpjProprietario : '',
      operadoras_permitidas: operadoras,
      limite_individual: limiteIndividual || '',
      limite_individual_cpf: limiteIndividualCpf !== null && user.role === 'usuario' ? limiteIndividualCpf : '',
      limite_individual_cpf_tem_cnpj: limiteIndividualCpfTemCnpj !== null && user.role === 'usuario' ? limiteIndividualCpfTemCnpj : '',
      pode_colocar_vencimento: user.pode_colocar_vencimento === 1 || user.pode_colocar_vencimento === true,
      pode_consultar_cpf: podeConsultarCpf,
      pode_consultar_cpf_tem_cnpj: podeConsultarCpfTemCnpj
    });
    
    // Carregar informações de limite se for proprietário
    if (user.role === 'proprietario') {
      try {
        const response = await axios.get(`/users/${user.id}/limite-info`);
        setLimiteInfo(response.data);
      } catch (err) {
        console.error('Erro ao carregar informações de limite:', err);
      }
    }
    
    setShowForm(false);
    setShowPasswordForm(false);
    setShowLogoForm(false);
  };

  useEffect(() => {
    // Quando o formulário for aberto e role for 'usuario', carregar operadoras disponíveis
    if (showForm && formData.role === 'usuario' && currentUser) {
      if (currentUser.role === 'proprietario') {
        // Proprietário: carregar suas próprias operadoras diretamente
        const operadoras = currentUser.operadoras_permitidas;
        if (operadoras) {
          try {
            const operadorasArray = typeof operadoras === 'string' ? JSON.parse(operadoras) : operadoras;
            const operadorasValidas = Array.isArray(operadorasArray) ? operadorasArray : [];
            if (operadorasValidas.length > 0) {
              setOperadorasDisponiveis(operadorasValidas);
            } else {
              setOperadorasDisponiveis([]);
            }
          } catch (e) {
            console.error('Erro ao parsear operadoras do proprietário:', e);
            setOperadorasDisponiveis([]);
          }
        } else {
          // Se não tem operadoras, tentar buscar do servidor
          atualizarOperadorasDisponiveis(currentUser.id);
        }
      } else if (currentUser.role === 'admin') {
        if (formData.criador_id) {
          atualizarOperadorasDisponiveis(formData.criador_id);
        } else {
          setOperadorasDisponiveis(['vivo', 'claro', 'tim', 'desktop', 'gigamais', 'vero', 'nio', 'brisanet', 'aonet', 'ligga', 'mhnet', 'ampernet', 'zaaz', 'uno']);
        }
      }
    }
  }, [showForm, formData.role, formData.criador_id, currentUser]);

  useEffect(() => {
    // Quando editar usuário e for proprietário, carregar operadoras
    if (editingUser && currentUser?.role === 'proprietario' && editingUser.role === 'usuario' && editingUser.criador_id === currentUser?.id) {
      const operadorasProprietario = currentUser.operadoras_permitidas;
      if (operadorasProprietario) {
        try {
          const operadorasArray = typeof operadorasProprietario === 'string' 
            ? JSON.parse(operadorasProprietario) 
            : operadorasProprietario;
          setOperadorasDisponiveis(Array.isArray(operadorasArray) ? operadorasArray : []);
        } catch (e) {
          setOperadorasDisponiveis([]);
        }
      }
    }
  }, [editingUser, currentUser?.role, currentUser?.id, currentUser?.operadoras_permitidas]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const dataToSend = { ...formData };
      delete dataToSend.password; // Não enviar senha na edição
      
      // Se for proprietário, vencimento é obrigatório
      if (dataToSend.role === 'proprietario' && !dataToSend.vencimento) {
        setError('Vencimento é obrigatório para proprietários');
        return;
      }

      // Validar limite_usuarios se for proprietário
      if (dataToSend.role === 'proprietario' && dataToSend.limite_usuarios) {
        const limite = parseInt(dataToSend.limite_usuarios);
        if (isNaN(limite) || limite < 1) {
          setError('Limite de usuários deve ser um número positivo');
          return;
        }
        dataToSend.limite_usuarios = limite;
      }

      // Validar limite_consultas se for proprietário
      if (dataToSend.role === 'proprietario' && dataToSend.limite_consultas) {
        const limite = parseInt(dataToSend.limite_consultas);
        if (isNaN(limite) || limite < 0) {
          setError('Limite de consultas deve ser um número não negativo');
          return;
        }
        dataToSend.limite_consultas = limite;
      } else if (dataToSend.role === 'proprietario') {
        // Se não fornecido, manter o existente ou null
        dataToSend.limite_consultas = dataToSend.limite_consultas === '' ? (editingUser.limite_consultas || null) : dataToSend.limite_consultas;
      }

      // Se for usuário, processar vencimento apenas se proprietário tiver permissão
      if (dataToSend.role === 'usuario') {
        // Se proprietário não tem permissão, forçar vencimento null
        if (currentUser?.role === 'proprietario' && !proprietarioPodeColocarVencimento) {
          dataToSend.vencimento = null;
        } else if (!dataToSend.vencimento) {
          // Se não especificou vencimento, definir como null
          dataToSend.vencimento = null;
        }
        dataToSend.limite_usuarios = null;
        dataToSend.limite_consultas = null;
        // Se não especificou criador, manter o existente
        if (!dataToSend.criador_id && !editingUser.criador_id) {
          delete dataToSend.criador_id;
        }
        // Se operadoras_permitidas está vazio, passar array vazio (não herdará do criador)
        if (!dataToSend.operadoras_permitidas || dataToSend.operadoras_permitidas.length === 0) {
          dataToSend.operadoras_permitidas = [];
        }
        
        // Se proprietário está editando, tratar limite individual separadamente
        const limiteIndividual = dataToSend.limite_individual;
        const limiteIndividualCpf = dataToSend.limite_individual_cpf;
        const limiteIndividualCpfTemCnpj = dataToSend.limite_individual_cpf_tem_cnpj;
        const podeConsultarCpf = dataToSend.pode_consultar_cpf;
        const podeConsultarCpfTemCnpj = dataToSend.pode_consultar_cpf_tem_cnpj;
      delete dataToSend.limite_individual; // Remover do dataToSend pois será enviado separadamente
      delete dataToSend.limite_individual_cpf;
      delete dataToSend.limite_individual_cpf_tem_cnpj;
      delete dataToSend.limite_consultas_cpf;
      delete dataToSend.pode_consultar_cpf;
      delete dataToSend.pode_consultar_cpf_tem_cnpj;
        
        // Atualizar usuário primeiro
        await axios.put(`/users/${editingUser.id}`, dataToSend);
        
        // Se proprietário está editando, atualizar limite individual
        if (isProprietario && editingUser.criador_id === currentUser.id) {
          try {
            const limiteValue = limiteIndividual === '' || limiteIndividual === null || limiteIndividual === undefined 
              ? null 
              : parseInt(limiteIndividual);
            if (limiteIndividual !== '' && limiteIndividual !== null && limiteIndividual !== undefined && isNaN(limiteValue)) {
              setError('Limite individual deve ser um número válido');
              return;
            }
            await axios.put(`/users/${currentUser.id}/usuarios/${editingUser.id}/limite-consultas`, {
              limite_consultas: limiteValue
            });
          } catch (err) {
            console.error('Erro ao atualizar limite individual:', err);
            // Continuar mesmo com erro no limite individual
          }
          
          // Atualizar limite individual de CPF
          try {
            const limiteCpfValue = limiteIndividualCpf === '' || limiteIndividualCpf === null || limiteIndividualCpf === undefined 
              ? null 
              : parseInt(limiteIndividualCpf);
            if (limiteIndividualCpf !== '' && limiteIndividualCpf !== null && limiteIndividualCpf !== undefined && isNaN(limiteCpfValue)) {
              setError('Limite individual de CPF deve ser um número válido');
              return;
            }
            await axios.put(`/users/${currentUser.id}/usuarios/${editingUser.id}/limite-consultas-cpf`, {
              limite_consultas: limiteCpfValue
            });
          } catch (err) {
            console.error('Erro ao atualizar limite individual de CPF:', err);
            // Continuar mesmo com erro no limite individual de CPF
          }
          
          // Atualizar limite individual de CPF TEM CNPJ
          try {
            const limiteCpfTemCnpjValue = limiteIndividualCpfTemCnpj === '' || limiteIndividualCpfTemCnpj === null || limiteIndividualCpfTemCnpj === undefined 
              ? null 
              : parseInt(limiteIndividualCpfTemCnpj);
            if (limiteIndividualCpfTemCnpj !== '' && limiteIndividualCpfTemCnpj !== null && limiteIndividualCpfTemCnpj !== undefined && isNaN(limiteCpfTemCnpjValue)) {
              setError('Limite individual de CPF TEM CNPJ deve ser um número válido');
              return;
            }
            await axios.put(`/users/${currentUser.id}/usuarios/${editingUser.id}/limite-consultas-cpf-tem-cnpj`, {
              limite_consultas: limiteCpfTemCnpjValue
            });
          } catch (err) {
            console.error('Erro ao atualizar limite individual de CPF TEM CNPJ:', err);
            // Continuar mesmo com erro no limite individual de CPF TEM CNPJ
          }
        } else if (isAdmin && editingUser.role === 'usuario' && editingUser.criador_id) {
          // Admin editando usuário: atualizar limites individuais
          try {
            const limiteValue = limiteIndividual === '' || limiteIndividual === null || limiteIndividual === undefined 
              ? null 
              : parseInt(limiteIndividual);
            if (limiteIndividual !== '' && limiteIndividual !== null && limiteIndividual !== undefined && isNaN(limiteValue)) {
              setError('Limite individual deve ser um número válido');
              return;
            }
            await axios.put(`/users/${editingUser.criador_id}/usuarios/${editingUser.id}/limite-consultas`, {
              limite_consultas: limiteValue
            });
          } catch (err) {
            console.error('Erro ao atualizar limite individual:', err);
          }
          
          // Atualizar limite individual de CPF
          try {
            const limiteCpfValue = limiteIndividualCpf === '' || limiteIndividualCpf === null || limiteIndividualCpf === undefined 
              ? null 
              : parseInt(limiteIndividualCpf);
            if (limiteIndividualCpf !== '' && limiteIndividualCpf !== null && limiteIndividualCpf !== undefined && isNaN(limiteCpfValue)) {
              setError('Limite individual de CPF deve ser um número válido');
              return;
            }
            await axios.put(`/users/${editingUser.criador_id}/usuarios/${editingUser.id}/limite-consultas-cpf`, {
              limite_consultas: limiteCpfValue
            });
          } catch (err) {
            console.error('Erro ao atualizar limite individual de CPF:', err);
          }
          
          // Atualizar limite individual de CPF TEM CNPJ
          try {
            const limiteCpfTemCnpjValue = limiteIndividualCpfTemCnpj === '' || limiteIndividualCpfTemCnpj === null || limiteIndividualCpfTemCnpj === undefined 
              ? null 
              : parseInt(limiteIndividualCpfTemCnpj);
            if (limiteIndividualCpfTemCnpj !== '' && limiteIndividualCpfTemCnpj !== null && limiteIndividualCpfTemCnpj !== undefined && isNaN(limiteCpfTemCnpjValue)) {
              setError('Limite individual de CPF TEM CNPJ deve ser um número válido');
              return;
            }
            await axios.put(`/users/${editingUser.criador_id}/usuarios/${editingUser.id}/limite-consultas-cpf-tem-cnpj`, {
              limite_consultas: limiteCpfTemCnpjValue
            });
          } catch (err) {
            console.error('Erro ao atualizar limite individual de CPF TEM CNPJ:', err);
          }
        }
        
        // Atualizar permissão de CPF (admin ou proprietário editando seus próprios usuários)
        if (isAdmin || (isProprietario && editingUser.role === 'usuario' && editingUser.criador_id === currentUser.id)) {
          try {
            await axios.put(`/users/${editingUser.id}/permissao-cpf`, {
              pode_consultar_cpf: podeConsultarCpf === true || podeConsultarCpf === 1
            });
          } catch (err) {
            console.error('Erro ao atualizar permissão de CPF:', err);
          }
          
          // Atualizar permissão de CPF TEM CNPJ
          try {
            await axios.put(`/users/${editingUser.id}/permissao-cpf-tem-cnpj`, {
              pode_consultar_cpf_tem_cnpj: podeConsultarCpfTemCnpj === true || podeConsultarCpfTemCnpj === 1
            });
          } catch (err) {
            console.error('Erro ao atualizar permissão de CPF TEM CNPJ:', err);
          }
        }
      } else {
        // Para proprietários e admins, remover criador_id
        delete dataToSend.criador_id;
        delete dataToSend.limite_individual;
        delete dataToSend.limite_individual_cpf;
        delete dataToSend.pode_consultar_cpf;
        delete dataToSend.pode_consultar_cpf_tem_cnpj;
        
        // Tratar limite de CPF do proprietário
        const limiteCpfProprietario = dataToSend.limite_consultas_cpf;
        const limiteCpfTemCnpjProprietario = dataToSend.limite_consultas_cpf_tem_cnpj;
        delete dataToSend.limite_consultas_cpf;
        delete dataToSend.limite_consultas_cpf_tem_cnpj;
        
        // Converter operadoras para array se necessário
        if (dataToSend.operadoras_permitidas && !Array.isArray(dataToSend.operadoras_permitidas)) {
          dataToSend.operadoras_permitidas = [];
        }
        // Apenas admin pode definir pode_colocar_vencimento para proprietários
        if (dataToSend.role === 'proprietario' && currentUser?.role !== 'admin') {
          delete dataToSend.pode_colocar_vencimento;
        }
        // Se não for proprietário, remover pode_colocar_vencimento
        if (dataToSend.role !== 'proprietario') {
          delete dataToSend.pode_colocar_vencimento;
        }
        await axios.put(`/users/${editingUser.id}`, dataToSend);
        
        // Atualizar limite de CPF do proprietário (apenas admin)
        if (dataToSend.role === 'proprietario' && isAdmin) {
          try {
            const limiteCpfValue = limiteCpfProprietario === '' || limiteCpfProprietario === null || limiteCpfProprietario === undefined 
              ? null 
              : parseInt(limiteCpfProprietario);
            if (limiteCpfProprietario !== '' && limiteCpfProprietario !== null && limiteCpfProprietario !== undefined && isNaN(limiteCpfValue)) {
              setError('Limite de consultas de CPF deve ser um número válido');
              return;
            }
            await axios.put(`/users/${editingUser.id}/limite-consultas-cpf`, {
              limite_consultas: limiteCpfValue
            });
          } catch (err) {
            console.error('Erro ao atualizar limite de CPF do proprietário:', err);
          }
          
          // Atualizar limite de CPF TEM CNPJ do proprietário
          try {
            const limiteCpfTemCnpjValue = limiteCpfTemCnpjProprietario === '' || limiteCpfTemCnpjProprietario === null || limiteCpfTemCnpjProprietario === undefined 
              ? null 
              : parseInt(limiteCpfTemCnpjProprietario);
            if (limiteCpfTemCnpjProprietario !== '' && limiteCpfTemCnpjProprietario !== null && limiteCpfTemCnpjProprietario !== undefined && isNaN(limiteCpfTemCnpjValue)) {
              setError('Limite de consultas de CPF TEM CNPJ deve ser um número válido');
              return;
            }
            await axios.put(`/users/${editingUser.id}/limite-consultas-cpf-tem-cnpj`, {
              limite_consultas: limiteCpfTemCnpjValue
            });
          } catch (err) {
            console.error('Erro ao atualizar limite de CPF TEM CNPJ do proprietário:', err);
          }
        }
        
        // Atualizar permissão de CPF (apenas admin) - para usuários e proprietários
        if (isAdmin && editingUser.role !== 'admin') {
          try {
            const podeCpf = formData.pode_consultar_cpf === true || formData.pode_consultar_cpf === 1;
            await axios.put(`/users/${editingUser.id}/permissao-cpf`, {
              pode_consultar_cpf: podeCpf
            });
          } catch (err) {
            console.error('Erro ao atualizar permissão de CPF:', err);
          }
          
          // Atualizar permissão de CPF TEM CNPJ
          try {
            const podeCpfTemCnpj = formData.pode_consultar_cpf_tem_cnpj === true || formData.pode_consultar_cpf_tem_cnpj === 1;
            await axios.put(`/users/${editingUser.id}/permissao-cpf-tem-cnpj`, {
              pode_consultar_cpf_tem_cnpj: podeCpfTemCnpj
            });
          } catch (err) {
            console.error('Erro ao atualizar permissão de CPF TEM CNPJ:', err);
          }
        }
      }

      setSuccess('Usuário atualizado com sucesso!');
      setFormData({ username: '', password: '', role: 'usuario', vencimento: '', criador_id: '', limite_usuarios: '', limite_consultas: '', limite_consultas_cpf: '', operadoras_permitidas: [], limite_individual: '', limite_individual_cpf: '', limite_individual_cpf_tem_cnpj: '', pode_colocar_vencimento: false, pode_consultar_cpf: false, pode_consultar_cpf_tem_cnpj: false });
      setEditingUser(null);
      loadUsers();
      loadCriadores();
      // Recarregar visualizações de limites
      if (currentUser?.role === 'admin') {
        loadProprietariosLimites();
        loadTodosLimites();
      } else if (currentUser?.role === 'proprietario') {
        loadUsuariosInfo();
        loadLimitesProprietario();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao atualizar usuário');
    }
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setLimiteInfo(null);
      setFormData({ username: '', password: '', role: 'usuario', vencimento: '', criador_id: '', limite_usuarios: '', limite_consultas: '', limite_consultas_cpf: '', operadoras_permitidas: [], limite_individual: '', limite_individual_cpf: '', limite_individual_cpf_tem_cnpj: '', pode_colocar_vencimento: false, pode_consultar_cpf: false, pode_consultar_cpf_tem_cnpj: false });
    setOperadorasDisponiveis([]);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este usuário?')) {
      return;
    }

    try {
      await axios.delete(`/users/${id}`);
      setSuccess('Usuário excluído com sucesso!');
      loadUsers();
      // Recarregar visualizações de limites
      if (currentUser?.role === 'admin') {
        loadProprietariosLimites();
        loadTodosLimites();
      } else if (currentUser?.role === 'proprietario') {
        loadUsuariosInfo();
        loadLimitesProprietario();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao excluir usuário');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR');
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const getRoleLabel = (role) => {
    const labels = {
      'admin': 'Admin',
      'proprietario': 'Proprietário',
      'usuario': 'Usuário'
    };
    return labels[role] || role;
  };

  const getRoleColor = (role) => {
    const colors = {
      'admin': '#cc0000',
      'proprietario': '#00cc66',
      'usuario': '#333'
    };
    return colors[role] || '#333';
  };

  const isVencido = (user) => {
    // Se o backend já forneceu a informação, usar ela
    if (user.vencido !== undefined) {
      return user.vencido;
    }
    
    // Caso contrário, calcular no frontend
    if (!user.vencimento) {
      return false;
    }
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const vencimento = new Date(user.vencimento);
    vencimento.setHours(0, 0, 0, 0);
    return vencimento < hoje;
  };

  const diasDesdeVencimento = (user) => {
    // Se o backend já forneceu a informação, usar ela
    if (user.diasDesdeVencimento !== undefined && user.diasDesdeVencimento !== null) {
      return user.diasDesdeVencimento;
    }
    
    // Caso contrário, calcular no frontend
    if (!user.vencimento) {
      return null;
    }
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const vencimento = new Date(user.vencimento);
    vencimento.setHours(0, 0, 0, 0);
    
    if (vencimento >= hoje) {
      return null; // Ainda não venceu
    }
    
    const diffTime = hoje - vencimento;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getRowStyle = (user) => {
    if (!isVencido(user)) {
      return {}; // Sem estilo especial se não estiver vencido
    }
    
    const dias = diasDesdeVencimento(user);
    
    // Vencido há mais de 7 dias (deveria ser excluído, mas se ainda aparecer, cor mais intensa)
    if (dias !== null && dias >= 7) {
      return {
        backgroundColor: 'rgba(204, 0, 0, 0.3)',
        borderLeft: '4px solid #cc0000'
      };
    }
    
    // Vencido há 0-7 dias (cor de alerta)
    if (dias !== null && dias >= 0) {
      return {
        backgroundColor: 'rgba(255, 102, 0, 0.2)',
        borderLeft: '4px solid #ff6600'
      };
    }
    
    return {};
  };

  const getVencimentoStatusText = (user) => {
    if (!isVencido(user)) {
      return null;
    }
    
    const dias = diasDesdeVencimento(user);
    if (dias === null) {
      return null;
    }
    
    if (dias === 0) {
      return 'Vencido hoje';
    } else if (dias === 1) {
      return 'Vencido há 1 dia';
    } else if (dias >= 7) {
      return `Vencido há ${dias} dias (será excluído)`;
    } else {
      return `Vencido há ${dias} dias`;
    }
  };

  return (
    <div>
      <nav className="nav">
        <Link to="/dashboard" className="nav-link">
          Dashboard
        </Link>
        <Link to="/consultas" className="nav-link">
          Consultas
        </Link>
        <Link to="/consulta-cpf" className="nav-link">
          Consulta CPF
        </Link>
        <Link to="/consulta-cpf-tem-cnpj" className="nav-link">
          Consulta CPF TEM CNPJ
        </Link>
        {(isAdmin || isProprietario) && (
          <Link to="/admin" className="nav-link active">
            Administração
          </Link>
        )}
        {(isAdmin || isProprietario) && (
          <Link to="/limites-consultas" className="nav-link">
            Limites
          </Link>
        )}
      </nav>

      <div className="card">
        <h2 style={{ color: '#cc0000' }}>Painel Administrativo</h2>
        <p style={{ color: '#999', marginBottom: '2rem' }}>
          {isAdmin ? 'Gerencie os usuários do sistema' : 'Gerencie suas configurações'}
        </p>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {/* Seção de Configurações do Admin */}
        <div style={{ marginBottom: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button
            style={{ backgroundColor: '#cc0000', color: '#fff', border: 'none' }}
            className="btn"
            onClick={() => {
              setShowForm(false);
              setShowPasswordForm(false);
              setShowProxyForm(false);
              setEditingUser(null);
              setShowLogoForm(!showLogoForm);
            }}
          >
            {showLogoForm ? 'Cancelar' : `📷 Upload Logo ${currentUser?.role === 'proprietario' ? 'Proprietário' : 'Admin'}`}
          </button>
          <button
            style={{ backgroundColor: '#cc0000', color: '#fff', border: 'none' }}
            className="btn"
            onClick={() => {
              setShowForm(false);
              setShowLogoForm(false);
              setShowProxyForm(false);
              setEditingUser(null);
              setShowPasswordForm(!showPasswordForm);
            }}
          >
            {showPasswordForm ? 'Cancelar' : '🔒 Alterar Senha'}
          </button>
          {isAdmin && (
            <button
              style={{ backgroundColor: '#cc0000', color: '#fff', border: 'none' }}
            className="btn"
              onClick={async () => {
                setShowForm(false);
                setShowLogoForm(false);
                setShowPasswordForm(false);
                setEditingUser(null);
                if (!showProxyForm) {
                  await loadProxyConfig();
                }
                setShowProxyForm(!showProxyForm);
              }}
            >
              {showProxyForm ? 'Cancelar' : '🌐 Configurar Proxy Melhor Plano'}
            </button>
          )}
          {(isAdmin || isProprietario) && (
              <button
                style={{ backgroundColor: '#cc0000', color: '#fff', border: 'none' }}
            className="btn"
                onClick={async () => {
                  setShowPasswordForm(false);
                  setShowLogoForm(false);
                  setEditingUser(null);
                  if (!showForm) {
                    // Resetar formulário ao abrir
                    setFormData({ username: '', password: '', role: 'usuario', vencimento: '', criador_id: '', limite_usuarios: '', limite_consultas: '', limite_consultas_cpf: '', operadoras_permitidas: [], limite_individual: '', limite_individual_cpf: '', limite_individual_cpf_tem_cnpj: '', pode_colocar_vencimento: false, pode_consultar_cpf: false, pode_consultar_cpf_tem_cnpj: false });
                    // Se proprietário, carregar suas operadoras imediatamente
                    if (currentUser?.role === 'proprietario') {
                      const operadorasProprietario = currentUser.operadoras_permitidas;
                      if (operadorasProprietario) {
                        try {
                          const operadorasArray = typeof operadorasProprietario === 'string' 
                            ? JSON.parse(operadorasProprietario) 
                            : operadorasProprietario;
                          setOperadorasDisponiveis(Array.isArray(operadorasArray) ? operadorasArray : []);
                        } catch (e) {
                          setOperadorasDisponiveis([]);
                        }
                      } else {
                        setOperadorasDisponiveis([]);
                      }
                    } else {
                      setOperadorasDisponiveis([]);
                    }
                  }
                  setShowForm(!showForm);
                }}
              >
                {showForm ? 'Cancelar' : '+ Criar Novo Usuário'}
              </button>
          )}
        </div>

        {/* Formulário de Upload de Logo */}
        {showLogoForm && (
          <div className="card" style={{ marginBottom: '2rem', backgroundColor: '#0a0a0a' }}>
            <h3 style={{ color: '#cc0000', marginBottom: '1rem' }}>Upload de Logo</h3>
            <form onSubmit={handleLogoUpload}>
              <div className="form-group">
                <label htmlFor="logo">Selecione uma imagem (JPEG, PNG, GIF, WEBP, SVG - máx. 5MB)</label>
                <input
                  type="file"
                  id="logo"
                  accept="image/*"
                  onChange={(e) => setLogoFile(e.target.files[0])}
                  required
                />
              </div>
              <button type="submit" style={{ backgroundColor: '#cc0000', color: '#fff', border: 'none' }}
className="btn">
                Enviar Logo
              </button>
            </form>
          </div>
        )}

        {/* Formulário de Mudança de Senha */}
        {showPasswordForm && (
          <div className="card" style={{ marginBottom: '2rem', backgroundColor: '#0a0a0a' }}>
            <h3 style={{ color: '#cc0000', marginBottom: '1rem' }}>Alterar Senha</h3>
            <form onSubmit={handlePasswordSubmit}>
              <div className="form-group">
                <label htmlFor="newPassword">Nova Senha</label>
                <input
                  type="password"
                  id="newPassword"
                  value={passwordData.newPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, newPassword: e.target.value })
                  }
                  placeholder="Digite a nova senha (mín. 6 caracteres)"
                  required
                  minLength="6"
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirmar Nova Senha</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                  }
                  placeholder="Confirme a nova senha"
                  required
                  minLength="6"
                />
              </div>
              <button type="submit" style={{ backgroundColor: '#cc0000', color: '#fff', border: 'none' }}
className="btn">
                Alterar Senha
              </button>
            </form>
          </div>
        )}

        {/* Formulário de Configuração de Proxy - Apenas Admin */}
        {showProxyForm && isAdmin && (
          <div className="card" style={{ marginBottom: '2rem', backgroundColor: '#0a0a0a' }}>
            <h3 style={{ color: '#cc0000', marginBottom: '1rem' }}>Configuração de Proxy - Melhor Plano</h3>
            
            {/* Status do Proxy */}
            {proxyConfig && proxyConfig.trim() !== '' && (
              <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#1a1a1a', borderRadius: '4px', border: '1px solid #333' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <label style={{ fontWeight: 'bold', color: '#ccc' }}>Status do Proxy:</label>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={testProxyConnection}
                    disabled={testingProxy || loadingProxy}
                    style={{ padding: '0.25rem 0.75rem', fontSize: '0.85rem' }}
                  >
                    {testingProxy ? 'Testando...' : '🔄 Testar Conexão'}
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {proxyStatus === 'connected' && (
                    <>
                      <span style={{ color: '#00cc66', fontSize: '1.2rem' }}>●</span>
                      <span style={{ color: '#00cc66' }}>Conectado</span>
                    </>
                  )}
                  {proxyStatus === 'disconnected' && (
                    <>
                      <span style={{ color: '#ff0000', fontSize: '1.2rem' }}>●</span>
                      <span style={{ color: '#ff0000' }}>Desconectado</span>
                    </>
                  )}
                  {proxyStatus === 'testing' && (
                    <>
                      <span style={{ color: '#ffaa00', fontSize: '1.2rem' }}>●</span>
                      <span style={{ color: '#ffaa00' }}>Testando...</span>
                    </>
                  )}
                  {proxyStatus === 'not_configured' && (
                    <>
                      <span style={{ color: '#999', fontSize: '1.2rem' }}>●</span>
                      <span style={{ color: '#999' }}>Não Configurado</span>
                    </>
                  )}
                  {proxyStatus === 'error' && (
                    <>
                      <span style={{ color: '#ff0000', fontSize: '1.2rem' }}>●</span>
                      <span style={{ color: '#ff0000' }}>Erro ao verificar</span>
                    </>
                  )}
                  {!proxyStatus && (
                    <>
                      <span style={{ color: '#999', fontSize: '1.2rem' }}>●</span>
                      <span style={{ color: '#999' }}>Carregando...</span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Seletor de Provedor de Proxy */}
            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label style={{ fontWeight: 'bold', color: '#ccc', display: 'block', marginBottom: '0.5rem' }}>Provedor de Proxy</label>
              <p style={{ fontSize: '0.85rem', color: '#999', marginBottom: '0.75rem' }}>
                Define como o IP é isolado/rotacionado em cada requisição (retry 429, 502). DataImpulse usa sessid no username; ProxyCheap usa _session na password.
              </p>
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="proxyProvider"
                    value="dataimpulse"
                    checked={proxyProvider === 'dataimpulse'}
                    onChange={() => handleProxyProviderChange('dataimpulse')}
                  />
                  <span style={{ color: '#ccc' }}>DataImpulse</span>
                  <span style={{ fontSize: '0.8rem', color: '#888' }}>(sessid no username)</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="proxyProvider"
                    value="proxycheap"
                    checked={proxyProvider === 'proxycheap'}
                    onChange={() => handleProxyProviderChange('proxycheap')}
                  />
                  <span style={{ color: '#ccc' }}>ProxyCheap</span>
                  <span style={{ fontSize: '0.8rem', color: '#888' }}>(_session na password)</span>
                </label>
              </div>
            </div>

            <form onSubmit={handleProxySubmit}>
              <div className="form-group">
                <label htmlFor="proxyConfig">Configuração de Proxy</label>
                <input
                  type="text"
                  id="proxyConfig"
                  value={proxyConfig}
                  onChange={(e) => setProxyConfig(e.target.value)}
                  placeholder="IP:PORT:USERNAME:PASSWORD"
                  disabled={loadingProxy}
                />
                <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.5rem' }}>
                  Formato: IP:PORT:USERNAME:PASSWORD
                  <br />
                  DataImpulse: gw.dataimpulse.com:823:be1f6ea389683d361d9b__cr.br:4e9d169e971ee536
                  <br />
                  ProxyCheap: proxy.proxy-cheap.com:31112:usuario:xyz123_country-BR_session-abc123
                  <br />
                  Deixe em branco para desabilitar o proxy.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button type="submit" style={{ backgroundColor: '#cc0000', color: '#fff', border: 'none' }}
className="btn" disabled={loadingProxy}>
                  {loadingProxy ? 'Carregando...' : 'Salvar Configuração'}
                </button>
                {proxyConfig && proxyConfig.trim() !== '' && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={testProxyConnection}
                    disabled={testingProxy || loadingProxy}
                  >
                    {testingProxy ? 'Testando...' : 'Testar Conexão'}
                  </button>
                )}
              </div>
            </form>

            {/* URL Servidor Claro (opcional) - sessão Claro sem Puppeteer local */}
            <hr style={{ margin: '1.5rem 0', borderColor: '#333' }} />
            <h4 style={{ color: '#ccc', marginBottom: '0.75rem' }}>URL Servidor Claro (opcional)</h4>
            <p style={{ fontSize: '0.85rem', color: '#999', marginBottom: '1rem' }}>
              Se configurado, a sessão Claro (cookie + fingerprint) será obtida desse servidor em vez de Puppeteer + proxy. Ex.: http://26.228.128.233:3015
            </p>
            <form onSubmit={handleClaroServidorSubmit}>
              <div className="form-group">
                <label htmlFor="claroServidorUrl">URL do Servidor Claro</label>
                <input
                  type="text"
                  id="claroServidorUrl"
                  value={claroServidorUrl}
                  onChange={(e) => setClaroServidorUrl(e.target.value)}
                  placeholder="http://26.228.128.233:3015"
                  disabled={loadingProxy}
                />
              </div>
              <button type="submit" className="btn btn-secondary" disabled={savingClaroServidor}>
                {savingClaroServidor ? 'Salvando...' : 'Salvar URL Servidor Claro'}
              </button>
            </form>
          </div>
        )}

        {/* Formulário de Edição de Usuário - Admin ou Proprietário */}
        {editingUser && (isAdmin || isProprietario) && (
          <div className="card" style={{ marginBottom: '2rem', backgroundColor: '#0a0a0a' }}>
            <h3 style={{ color: '#cc0000', marginBottom: '1rem' }}>Editar Usuário</h3>
            <form onSubmit={handleUpdate}>
              <div className="form-group">
                <label htmlFor="edit-username">Usuário</label>
                <input
                  type="text"
                  id="edit-username"
                  value={formData.username}
                  onChange={(e) =>
                    setFormData({ ...formData, username: e.target.value })
                  }
                  placeholder="Digite o nome de usuário"
                  required
                />
              </div>

              {isAdmin && (
                <div className="form-group">
                  <label htmlFor="edit-role">Tipo de Usuário</label>
                  <select
                    id="edit-role"
                    value={formData.role}
                    onChange={(e) => {
                      const newRole = e.target.value;
                      setFormData({ 
                        ...formData, 
                        role: newRole,
                        vencimento: newRole === 'proprietario' ? formData.vencimento : '',
                        criador_id: newRole === 'usuario' ? formData.criador_id : ''
                      });
                    }}
                  >
                    <option value="proprietario">Proprietário</option>
                    <option value="usuario">Usuário</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              )}

              {formData.role === 'proprietario' && (
                <>
                  <div className="form-group">
                    <label htmlFor="edit-vencimento">Data de Vencimento (obrigatório)</label>
                    <input
                      type="date"
                      id="edit-vencimento"
                      value={formData.vencimento}
                      onChange={(e) =>
                        setFormData({ ...formData, vencimento: e.target.value })
                      }
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="edit-limite_usuarios">Limite de Usuários</label>
                    <input
                      type="number"
                      id="edit-limite_usuarios"
                      min="1"
                      value={formData.limite_usuarios}
                      onChange={(e) =>
                        setFormData({ ...formData, limite_usuarios: e.target.value })
                      }
                      placeholder="Quantidade máxima de usuários que pode criar"
                    />
                    {limiteInfo && (
                      <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.5rem' }}>
                        Usuários criados: {limiteInfo.criados} / {limiteInfo.limite || 'Sem limite'}
                        {limiteInfo.restantes !== null && ` (${limiteInfo.restantes} restantes)`}
                      </p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="edit-limite_consultas">Limite de Consultas por Mês</label>
                    <input
                      type="number"
                      id="edit-limite_consultas"
                      min="0"
                      value={formData.limite_consultas}
                      onChange={(e) =>
                        setFormData({ ...formData, limite_consultas: e.target.value })
                      }
                      placeholder="Quantidade máxima de consultas por mês"
                    />
                    <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                      Deixe em branco para sem limite. O limite é resetado no dia 1 de cada mês.
                    </p>
                  </div>

                  {isAdmin && (
                    <>
                      <div className="form-group">
                        <label htmlFor="edit-limite_consultas_cpf">Limite de Consultas de CPF por Mês</label>
                        <input
                          type="number"
                          id="edit-limite_consultas_cpf"
                          min="0"
                          value={formData.limite_consultas_cpf || ''}
                          onChange={(e) =>
                            setFormData({ ...formData, limite_consultas_cpf: e.target.value })
                          }
                          placeholder="Quantidade máxima de consultas de CPF por mês"
                        />
                        <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                          Deixe em branco para sem limite. O limite é resetado no dia 1 de cada mês.
                        </p>
                      </div>
                      <div className="form-group">
                        <label htmlFor="edit-limite_consultas_cpf_tem_cnpj">Limite de Consultas de CPF TEM CNPJ por Mês</label>
                        <input
                          type="number"
                          id="edit-limite_consultas_cpf_tem_cnpj"
                          min="0"
                          value={formData.limite_consultas_cpf_tem_cnpj || ''}
                          onChange={(e) =>
                            setFormData({ ...formData, limite_consultas_cpf_tem_cnpj: e.target.value })
                          }
                          placeholder="Quantidade máxima de consultas de CPF TEM CNPJ por mês"
                        />
                        <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                          Deixe em branco para sem limite. O limite é resetado no dia 1 de cada mês.
                        </p>
                      </div>
                    </>
                  )}

                  <div className="form-group">
                    <label>Operadoras Permitidas</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
                      {['vivo', 'claro', 'tim', 'desktop', 'gigamais', 'vero', 'nio', 'brisanet', 'aonet', 'ligga', 'mhnet', 'ampernet', 'zaaz', 'uno'].map(op => (
                        <label key={op} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={formData.operadoras_permitidas.includes(op)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  operadoras_permitidas: [...formData.operadoras_permitidas, op]
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  operadoras_permitidas: formData.operadoras_permitidas.filter(o => o !== op)
                                });
                              }
                            }}
                          />
                          <span style={{ textTransform: 'uppercase' }}>{op}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {isAdmin && (
                    <>
                      <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={formData.pode_colocar_vencimento}
                            onChange={(e) =>
                              setFormData({ ...formData, pode_colocar_vencimento: e.target.checked })
                            }
                          />
                          <span>Permitir definir vencimento para usuários</span>
                        </label>
                        <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                          Se marcado, este proprietário poderá definir data de vencimento para os usuários que criar.
                        </p>
                      </div>
                      <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={formData.pode_consultar_cpf}
                            onChange={(e) =>
                              setFormData({ ...formData, pode_consultar_cpf: e.target.checked })
                            }
                          />
                          <span>Permitir consultar CPF</span>
                        </label>
                        <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                          Se marcado, este proprietário poderá realizar consultas de CPF.
                        </p>
                      </div>
                      <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={formData.pode_consultar_cpf_tem_cnpj}
                            onChange={(e) =>
                              setFormData({ ...formData, pode_consultar_cpf_tem_cnpj: e.target.checked })
                            }
                          />
                          <span>Permitir consultar CPF TEM CNPJ</span>
                        </label>
                        <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                          Se marcado, este proprietário poderá realizar consultas de CPF TEM CNPJ.
                        </p>
                      </div>
                    </>
                  )}
                </>
              )}

              {formData.role === 'usuario' && isAdmin && (
                <div className="form-group">
                  <label htmlFor="edit-criador_id">Criador</label>
                  <select
                    id="edit-criador_id"
                    value={formData.criador_id}
                    onChange={(e) =>
                      setFormData({ ...formData, criador_id: e.target.value })
                    }
                  >
                    <option value="">Nenhum</option>
                    {criadores.map((criador) => (
                      <option key={criador.id} value={criador.id}>
                        {criador.username} ({getRoleLabel(criador.role)})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formData.role === 'usuario' && (isAdmin || isProprietario) && (
                <>
                  <div className="form-group">
                    <label>Operadoras Permitidas</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
                      {operadorasDisponiveis.length > 0 ? (
                        operadorasDisponiveis.map(op => (
                          <label key={op} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={formData.operadoras_permitidas.includes(op)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData({
                                    ...formData,
                                    operadoras_permitidas: [...formData.operadoras_permitidas, op]
                                  });
                                } else {
                                  setFormData({
                                    ...formData,
                                    operadoras_permitidas: formData.operadoras_permitidas.filter(o => o !== op)
                                  });
                                }
                              }}
                            />
                            <span style={{ textTransform: 'uppercase' }}>{op}</span>
                          </label>
                        ))
                      ) : (
                        <p style={{ color: '#999', fontSize: '0.85rem' }}>
                          {isProprietario 
                            ? 'Carregando operadoras disponíveis...'
                            : 'Nenhuma operadora disponível'}
                        </p>
                      )}
                    </div>
                    <p style={{ fontSize: '0.85rem', color: '#ff6600', marginTop: '0.5rem' }}>
                      ⚠️ Selecione pelo menos uma operadora. Se nenhuma for selecionada, o usuário NÃO poderá realizar consultas.
                    </p>
                  </div>
                  
                  {isProprietario && proprietarioPodeColocarVencimento && (
                    <div className="form-group">
                      <label htmlFor="edit-vencimento-usuario">Data de Vencimento (opcional)</label>
                      <input
                        type="date"
                        id="edit-vencimento-usuario"
                        value={formData.vencimento || ''}
                        onChange={(e) =>
                          setFormData({ ...formData, vencimento: e.target.value })
                        }
                      />
                      <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                        Defina uma data de vencimento para este usuário (opcional).
                      </p>
                    </div>
                  )}
                  
                  {isProprietario && (
                    <div className="form-group">
                      <label htmlFor="edit-limite-individual">Limite Individual de Consultas (opcional)</label>
                      <input
                        type="number"
                        id="edit-limite-individual"
                        min="0"
                        value={formData.limite_individual || ''}
                        onChange={(e) =>
                          setFormData({ ...formData, limite_individual: e.target.value })
                        }
                        placeholder="Deixe em branco para usar pool compartilhado"
                      />
                      <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                        Se definido, este usuário terá um limite individual. Se não definido, usará o pool compartilhado do proprietário.
                      </p>
                    </div>
                  )}
                  
                  {(isAdmin || (isProprietario && editingUser.role === 'usuario' && editingUser.criador_id === currentUser?.id)) && (
                    <div className="form-group">
                      <label htmlFor="edit-limite-individual-cpf">Limite Individual de Consultas de CPF (opcional)</label>
                      <input
                        type="number"
                        id="edit-limite-individual-cpf"
                        min="0"
                        value={formData.limite_individual_cpf || ''}
                        onChange={(e) =>
                          setFormData({ ...formData, limite_individual_cpf: e.target.value })
                        }
                        placeholder="Deixe em branco para usar pool compartilhado"
                      />
                      <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                        Se definido, este usuário terá um limite individual de CPF. Se não definido, usará o pool compartilhado do proprietário.
                      </p>
                    </div>
                  )}
                  
                  {(isAdmin || (isProprietario && editingUser.role === 'usuario' && editingUser.criador_id === currentUser?.id)) && (
                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={formData.pode_consultar_cpf}
                          onChange={(e) =>
                            setFormData({ ...formData, pode_consultar_cpf: e.target.checked })
                          }
                        />
                        <span>Permitir consultar CPF</span>
                      </label>
                      <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                        Se marcado, este usuário poderá realizar consultas de CPF.
                      </p>
                    </div>
                  )}
                  
                  {(isAdmin || (isProprietario && editingUser.role === 'usuario' && editingUser.criador_id === currentUser?.id)) && (
                    <div className="form-group">
                      <label htmlFor="edit-limite-individual-cpf-tem-cnpj">Limite Individual de Consultas de CPF TEM CNPJ (opcional)</label>
                      <input
                        type="number"
                        id="edit-limite-individual-cpf-tem-cnpj"
                        min="0"
                        value={formData.limite_individual_cpf_tem_cnpj || ''}
                        onChange={(e) =>
                          setFormData({ ...formData, limite_individual_cpf_tem_cnpj: e.target.value })
                        }
                        placeholder="Deixe em branco para usar pool compartilhado"
                      />
                      <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                        Se definido, este usuário terá um limite individual de CPF TEM CNPJ. Se não definido, usará o pool compartilhado do proprietário.
                      </p>
                    </div>
                  )}
                  
                  {(isAdmin || (isProprietario && editingUser.role === 'usuario' && editingUser.criador_id === currentUser?.id)) && (
                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={formData.pode_consultar_cpf_tem_cnpj}
                          onChange={(e) =>
                            setFormData({ ...formData, pode_consultar_cpf_tem_cnpj: e.target.checked })
                          }
                        />
                        <span>Permitir consultar CPF TEM CNPJ</span>
                      </label>
                      <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                        Se marcado, este usuário poderá realizar consultas de CPF TEM CNPJ.
                      </p>
                    </div>
                  )}
                </>
              )}

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button type="submit" style={{ backgroundColor: '#cc0000', color: '#fff', border: 'none' }}
className="btn">
                  Salvar Alterações
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleCancelEdit}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {showForm && (
          <div className="card" style={{ marginBottom: '2rem', backgroundColor: '#0a0a0a' }}>
            <h3 style={{ color: '#cc0000', marginBottom: '1rem' }}>Criar Novo Usuário</h3>
            {isProprietario && limiteInfoAtual && !limiteInfoAtual.podeCriar && limiteInfoAtual.limite !== null && (
              <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
                <strong>⚠️ Limite Atingido</strong>
                <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                  Você já criou {limiteInfoAtual.criados} de {limiteInfoAtual.limite} usuários permitidos. 
                  Não é possível criar mais usuários até que o limite seja aumentado.
                </p>
              </div>
            )}
            {isProprietario && limiteInfoAtual && limiteInfoAtual.podeCriar && limiteInfoAtual.limite !== null && (
              <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
                <strong>ℹ️ Limite de Usuários</strong>
                <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                  Você criou {limiteInfoAtual.criados} de {limiteInfoAtual.limite} usuários permitidos. 
                  Restam {limiteInfoAtual.restantes} usuários.
                </p>
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="username">Usuário</label>
                <input
                  type="text"
                  id="username"
                  value={formData.username}
                  onChange={(e) =>
                    setFormData({ ...formData, username: e.target.value })
                  }
                  placeholder="Digite o nome de usuário"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Senha</label>
                <input
                  type="password"
                  id="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  placeholder="Digite a senha"
                  required
                  minLength="6"
                />
              </div>

              <div className="form-group">
                <label htmlFor="role">Tipo de Usuário</label>
                <select
                  id="role"
                  value={formData.role}
                  onChange={async (e) => {
                    const newRole = e.target.value;
                    setFormData({ 
                      ...formData, 
                      role: newRole,
                      vencimento: newRole === 'proprietario' ? formData.vencimento : '',
                      criador_id: newRole === 'usuario' ? formData.criador_id : '',
                      operadoras_permitidas: []
                    });
                    // Atualizar operadoras disponíveis quando mudar o role
                    if (newRole === 'usuario') {
                      const criadorId = formData.criador_id || (currentUser?.role === 'admin' ? '' : currentUser?.id || '');
                      await atualizarOperadorasDisponiveis(criadorId);
                    } else {
                      setOperadorasDisponiveis(['vivo', 'claro', 'tim', 'desktop', 'gigamais', 'vero', 'nio', 'brisanet', 'aonet', 'ligga', 'mhnet', 'ampernet', 'zaaz', 'uno']);
                    }
                  }}
                >
                  {isAdmin && <option value="proprietario">Proprietário</option>}
                  <option value="usuario">Usuário</option>
                  {isAdmin && <option value="admin">Admin</option>}
                </select>
              </div>

              {formData.role === 'proprietario' && (
                <>
                  <div className="form-group">
                    <label htmlFor="vencimento">Data de Vencimento (obrigatório)</label>
                    <input
                      type="date"
                      id="vencimento"
                      value={formData.vencimento}
                      onChange={(e) =>
                        setFormData({ ...formData, vencimento: e.target.value })
                      }
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="limite_usuarios">Limite de Usuários</label>
                    <input
                      type="number"
                      id="limite_usuarios"
                      min="1"
                      value={formData.limite_usuarios}
                      onChange={(e) =>
                        setFormData({ ...formData, limite_usuarios: e.target.value })
                      }
                      placeholder="Quantidade máxima de usuários que pode criar"
                    />
                    <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                      Deixe em branco para sem limite
                    </p>
                  </div>

                  <div className="form-group">
                    <label htmlFor="limite_consultas">Limite de Consultas por Mês</label>
                    <input
                      type="number"
                      id="limite_consultas"
                      min="0"
                      value={formData.limite_consultas}
                      onChange={(e) =>
                        setFormData({ ...formData, limite_consultas: e.target.value })
                      }
                      placeholder="Quantidade máxima de consultas por mês"
                    />
                    <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                      Deixe em branco para sem limite. O limite é resetado no dia 1 de cada mês.
                    </p>
                  </div>

                  {isAdmin && (
                    <div className="form-group">
                      <label htmlFor="limite_consultas_cpf">Limite de Consultas de CPF por Mês</label>
                      <input
                        type="number"
                        id="limite_consultas_cpf"
                        min="0"
                        value={formData.limite_consultas_cpf || ''}
                        onChange={(e) =>
                          setFormData({ ...formData, limite_consultas_cpf: e.target.value })
                        }
                        placeholder="Quantidade máxima de consultas de CPF por mês"
                      />
                      <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                        Deixe em branco para sem limite. O limite é resetado no dia 1 de cada mês.
                      </p>
                    </div>
                  )}

                  <div className="form-group">
                    <label>Operadoras Permitidas</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
                      {['vivo', 'claro', 'tim', 'desktop', 'gigamais', 'vero', 'nio', 'brisanet', 'aonet', 'ligga', 'mhnet', 'ampernet', 'zaaz', 'uno'].map(op => (
                        <label key={op} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={formData.operadoras_permitidas.includes(op)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  operadoras_permitidas: [...formData.operadoras_permitidas, op]
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  operadoras_permitidas: formData.operadoras_permitidas.filter(o => o !== op)
                                });
                              }
                            }}
                          />
                          <span style={{ textTransform: 'uppercase' }}>{op}</span>
                        </label>
                      ))}
                    </div>
                    <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.5rem' }}>
                      Selecione quais operadoras este proprietário pode consultar
                    </p>
                  </div>

                  {isAdmin && (
                    <>
                      <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={formData.pode_colocar_vencimento}
                            onChange={(e) =>
                              setFormData({ ...formData, pode_colocar_vencimento: e.target.checked })
                            }
                          />
                          <span>Permitir definir vencimento para usuários</span>
                        </label>
                        <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                          Se marcado, este proprietário poderá definir data de vencimento para os usuários que criar.
                        </p>
                      </div>
                      <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={formData.pode_consultar_cpf}
                            onChange={(e) =>
                              setFormData({ ...formData, pode_consultar_cpf: e.target.checked })
                            }
                          />
                          <span>Permitir consultar CPF</span>
                        </label>
                        <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                          Se marcado, este proprietário poderá realizar consultas de CPF.
                        </p>
                      </div>
                      <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={formData.pode_consultar_cpf_tem_cnpj}
                            onChange={(e) =>
                              setFormData({ ...formData, pode_consultar_cpf_tem_cnpj: e.target.checked })
                            }
                          />
                          <span>Permitir consultar CPF TEM CNPJ</span>
                        </label>
                        <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                          Se marcado, este proprietário poderá realizar consultas de CPF TEM CNPJ.
                        </p>
                      </div>
                    </>
                  )}
                </>
              )}

              {formData.role === 'usuario' && isAdmin && (
                <div className="form-group">
                  <label htmlFor="criador_id">Criador (opcional - será você se não especificar)</label>
                  <select
                    id="criador_id"
                    value={formData.criador_id}
                    onChange={async (e) => {
                      const criadorId = e.target.value;
                      setFormData({ ...formData, criador_id: criadorId, operadoras_permitidas: [] });
                      await atualizarOperadorasDisponiveis(criadorId);
                    }}
                  >
                    <option value="">Eu (admin logado)</option>
                    {criadores.map((criador) => (
                      <option key={criador.id} value={criador.id}>
                        {criador.username} ({getRoleLabel(criador.role)})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formData.role === 'usuario' && (
                <div className="form-group">
                  <label>Operadoras Permitidas</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
                    {operadorasDisponiveis.length > 0 ? (
                      operadorasDisponiveis.map(op => (
                        <label key={op} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={formData.operadoras_permitidas.includes(op)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  operadoras_permitidas: [...formData.operadoras_permitidas, op]
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  operadoras_permitidas: formData.operadoras_permitidas.filter(o => o !== op)
                                });
                              }
                            }}
                          />
                          <span style={{ textTransform: 'uppercase' }}>{op}</span>
                        </label>
                      ))
                    ) : (
                      <p style={{ color: '#999', fontSize: '0.85rem' }}>
                        {formData.role === 'usuario' && !formData.criador_id && isAdmin
                          ? 'Selecione um criador para ver as operadoras disponíveis'
                          : formData.role === 'usuario' && isProprietario
                          ? 'Carregando operadoras disponíveis...'
                          : 'Nenhuma operadora disponível'}
                      </p>
                    )}
                  </div>
                  <p style={{ fontSize: '0.85rem', color: '#ff6600', marginTop: '0.5rem' }}>
                    ⚠️ Selecione pelo menos uma operadora. Se nenhuma for selecionada, o usuário NÃO poderá realizar consultas.
                  </p>
                </div>
              )}

              {isProprietario && proprietarioPodeColocarVencimento && formData.role === 'usuario' && (
                <div className="form-group">
                  <label htmlFor="vencimento-usuario">Data de Vencimento (opcional)</label>
                  <input
                    type="date"
                    id="vencimento-usuario"
                    value={formData.vencimento || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, vencimento: e.target.value })
                    }
                  />
                  <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                    Defina uma data de vencimento para este usuário (opcional).
                  </p>
                </div>
              )}

              {((isAdmin && formData.role === 'usuario') || (isProprietario && formData.role === 'usuario')) && (
                <div className="form-group">
                  <label htmlFor="limite-individual-cpf">Limite Individual de Consultas de CPF (opcional)</label>
                  <input
                    type="number"
                    id="limite-individual-cpf"
                    min="0"
                    value={formData.limite_individual_cpf || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, limite_individual_cpf: e.target.value })
                    }
                    placeholder="Deixe em branco para usar pool compartilhado"
                  />
                  <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                    Se definido, este usuário terá um limite individual de CPF. Se não definido, usará o pool compartilhado do proprietário.
                  </p>
                </div>
              )}

              {((isAdmin && formData.role === 'usuario') || (isProprietario && formData.role === 'usuario')) && (
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.pode_consultar_cpf}
                      onChange={(e) =>
                        setFormData({ ...formData, pode_consultar_cpf: e.target.checked })
                      }
                    />
                    <span>Permitir consultar CPF</span>
                  </label>
                  <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                    Se marcado, este usuário poderá realizar consultas de CPF.
                  </p>
                </div>
              )}

              {((isAdmin && formData.role === 'usuario') || (isProprietario && formData.role === 'usuario')) && (
                <div className="form-group">
                  <label htmlFor="limite-individual-cpf-tem-cnpj">Limite Individual de Consultas de CPF TEM CNPJ (opcional)</label>
                  <input
                    type="number"
                    id="limite-individual-cpf-tem-cnpj"
                    min="0"
                    value={formData.limite_individual_cpf_tem_cnpj || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, limite_individual_cpf_tem_cnpj: e.target.value })
                    }
                    placeholder="Deixe em branco para usar pool compartilhado"
                  />
                  <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                    Se definido, este usuário terá um limite individual de CPF TEM CNPJ. Se não definido, usará o pool compartilhado do proprietário.
                  </p>
                </div>
              )}

              {((isAdmin && formData.role === 'usuario') || (isProprietario && formData.role === 'usuario')) && (
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.pode_consultar_cpf_tem_cnpj}
                      onChange={(e) =>
                        setFormData({ ...formData, pode_consultar_cpf_tem_cnpj: e.target.checked })
                      }
                    />
                    <span>Permitir consultar CPF TEM CNPJ</span>
                  </label>
                  <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem' }}>
                    Se marcado, este usuário poderá realizar consultas de CPF TEM CNPJ.
                  </p>
                </div>
              )}

              <button 
                type="submit" 
                style={{ backgroundColor: '#cc0000', color: '#fff', border: 'none' }}
                className="btn"
                disabled={isProprietario && limiteInfoAtual && !limiteInfoAtual.podeCriar && limiteInfoAtual.limite !== null}
              >
                Criar Usuário
              </button>
            </form>
          </div>
        )}

        {/* Tabela de Usuários - Admin ou Proprietário */}
        {(isAdmin || isProprietario) && (
          <>
            <h3 style={{ color: '#cc0000', marginBottom: '1rem' }}>
              {isAdmin ? 'Usuários Cadastrados' : 'Meus Usuários'}
            </h3>

            {loading ? (
              <div className="loading">Carregando usuários...</div>
            ) : users.length === 0 ? (
              <p style={{ color: '#999' }}>Nenhum usuário cadastrado ainda.</p>
            ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th style={{ color: '#cc0000' }}>ID</th>
                  <th style={{ color: '#cc0000' }}>Usuário</th>
                  <th style={{ color: '#cc0000' }}>Role</th>
                  <th style={{ color: '#cc0000' }}>Logo</th>
                  <th style={{ color: '#cc0000' }}>Vencimento</th>
                  <th style={{ color: '#cc0000' }}>Criador</th>
                  <th style={{ color: '#cc0000' }}>Data de Criação</th>
                  <th style={{ color: '#cc0000' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const statusText = getVencimentoStatusText(user);
                  return (
                    <tr key={user.id} style={getRowStyle(user)}>
                      <td>{user.id}</td>
                      <td>{user.username}</td>
                      <td>
                        <span
                          style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            backgroundColor: getRoleColor(user.role),
                            color: '#fff',
                            fontSize: '0.85rem'
                          }}
                        >
                          {getRoleLabel(user.role)}
                        </span>
                      </td>
                      <td>
                        {user.logo_path ? (
                          <LogoDisplay logoPath={user.logo_path} style={{ maxHeight: '30px', maxWidth: '80px' }} />
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        <div>
                          {formatDateOnly(user.vencimento)}
                          {statusText && (
                            <div style={{ 
                              marginTop: '0.25rem', 
                              fontSize: '0.75rem', 
                              color: diasDesdeVencimento(user) >= 7 ? '#cc0000' : '#ff6600',
                              fontWeight: 'bold'
                            }}>
                              {statusText}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        {user.criador_username ? (
                          <span style={{ fontSize: '0.85rem' }}>
                            {user.criador_username} ({getRoleLabel(user.criador_role)})
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>{formatDate(user.created_at)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            style={{ backgroundColor: '#cc0000', color: '#fff', border: 'none', padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                            className="btn"
                            onClick={() => handleEdit(user)}
                          >
                            Editar
                          </button>
                          <button
                            className="btn btn-danger"
                            onClick={() => handleDelete(user.id)}
                            style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                          >
                            Excluir
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
          </>
        )}

        {/* Visualização de Limites de Proprietários - Apenas Admin */}
        {isAdmin && (
          <div className="card" style={{ marginTop: '2rem' }}>
            <h3 style={{ color: '#cc0000', marginBottom: '1rem' }}>Limites dos Proprietários</h3>
            {loadingLimites ? (
              <div className="loading">Carregando limites...</div>
            ) : proprietariosLimites.length === 0 ? (
              <p style={{ color: '#999' }}>Nenhum proprietário cadastrado ainda.</p>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th style={{ color: '#cc0000' }}>ID</th>
                      <th style={{ color: '#cc0000' }}>Usuário</th>
                      <th style={{ color: '#cc0000' }}>Vencimento</th>
                      <th style={{ color: '#cc0000' }}>Limite Usuários</th>
                      <th style={{ color: '#cc0000' }}>Usuários Criados</th>
                      <th style={{ color: '#cc0000' }}>Usuários Restantes</th>
                      <th style={{ color: '#cc0000' }}>Limite Consultas</th>
                      <th style={{ color: '#cc0000' }}>Consultas Usadas</th>
                      <th style={{ color: '#cc0000' }}>Consultas Restantes</th>
                      <th style={{ color: '#cc0000' }}>Limite CPF</th>
                      <th style={{ color: '#cc0000' }}>CPF Usadas</th>
                      <th style={{ color: '#cc0000' }}>CPF Restantes</th>
                      <th style={{ color: '#cc0000' }}>Limite CPF TEM CNPJ</th>
                      <th style={{ color: '#cc0000' }}>CPF TEM CNPJ Usadas</th>
                      <th style={{ color: '#cc0000' }}>CPF TEM CNPJ Restantes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proprietariosLimites.map((prop) => {
                      const statusText = getVencimentoStatusText(prop);
                      return (
                        <tr key={prop.id} style={getRowStyle(prop)}>
                          <td>{prop.id}</td>
                          <td>{prop.username}</td>
                          <td>
                            <div>
                              {formatDateOnly(prop.vencimento)}
                              {statusText && (
                                <div style={{ 
                                  marginTop: '0.25rem', 
                                  fontSize: '0.75rem', 
                                  color: diasDesdeVencimento(prop) >= 7 ? '#cc0000' : '#ff6600',
                                  fontWeight: 'bold'
                                }}>
                                  {statusText}
                                </div>
                              )}
                            </div>
                          </td>
                          <td>{prop.limite_usuarios !== null ? prop.limite_usuarios : 'Ilimitado'}</td>
                          <td>{prop.usuarios_criados}</td>
                          <td>{prop.usuarios_restantes !== null ? prop.usuarios_restantes : '-'}</td>
                          <td>{prop.limite_consultas !== null ? prop.limite_consultas : 'Ilimitado'}</td>
                          <td>{prop.consultas_usadas}</td>
                          <td>
                            {prop.consultas_restantes !== null ? (
                              <span style={{
                                color: prop.consultas_restantes === 0 ? '#ff0000' : 
                                       prop.consultas_restantes <= (prop.limite_consultas || 0) * 0.1 ? '#ff6600' : '#00cc66'
                              }}>
                                {prop.consultas_restantes}
                              </span>
                            ) : (
                              'Ilimitado'
                            )}
                          </td>
                          <td>
                            {prop.limite_cpf === -1 ? (
                              <span style={{ color: '#ff0000', fontWeight: 'bold' }}>Sem permissão</span>
                            ) : prop.limite_cpf !== null ? (
                              prop.limite_cpf
                            ) : (
                              'Sem limite'
                            )}
                          </td>
                          <td>{prop.consultas_cpf_usadas || 0}</td>
                          <td>
                            {prop.consultas_cpf_restantes === -1 ? (
                              <span style={{ color: '#ff0000', fontWeight: 'bold' }}>Sem permissão</span>
                            ) : prop.consultas_cpf_restantes !== null ? (
                              <span style={{
                                color: prop.consultas_cpf_restantes === 0 ? '#ff0000' : 
                                       prop.consultas_cpf_restantes <= (prop.limite_cpf || 0) * 0.1 ? '#ff6600' : '#00cc66'
                              }}>
                                {prop.consultas_cpf_restantes}
                              </span>
                            ) : (
                              'Sem limite'
                            )}
                          </td>
                          <td>
                            {prop.limite_cpf_tem_cnpj === -1 ? (
                              <span style={{ color: '#ff0000', fontWeight: 'bold' }}>Sem permissão</span>
                            ) : prop.limite_cpf_tem_cnpj !== null ? (
                              prop.limite_cpf_tem_cnpj
                            ) : (
                              'Sem limite'
                            )}
                          </td>
                          <td>{prop.consultas_cpf_tem_cnpj_usadas || 0}</td>
                          <td>
                            {prop.consultas_cpf_tem_cnpj_restantes === -1 ? (
                              <span style={{ color: '#ff0000', fontWeight: 'bold' }}>Sem permissão</span>
                            ) : prop.consultas_cpf_tem_cnpj_restantes !== null ? (
                              <span style={{
                                color: prop.consultas_cpf_tem_cnpj_restantes === 0 ? '#ff0000' : 
                                       prop.consultas_cpf_tem_cnpj_restantes <= (prop.limite_cpf_tem_cnpj || 0) * 0.1 ? '#ff6600' : '#00cc66'
                              }}>
                                {prop.consultas_cpf_tem_cnpj_restantes}
                              </span>
                            ) : (
                              'Sem limite'
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Visualização de TODOS os Usuários com Limites - Apenas Admin */}
        {isAdmin && (
          <div className="card" style={{ marginTop: '2rem' }}>
            <h3 style={{ color: '#cc0000', marginBottom: '1rem' }}>Limites de Todos os Usuários e Proprietários</h3>
            {loadingLimites ? (
              <div className="loading">Carregando limites...</div>
            ) : todosLimites.length === 0 ? (
              <p style={{ color: '#999' }}>Nenhum usuário ou proprietário cadastrado ainda.</p>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th style={{ color: '#cc0000' }}>ID</th>
                      <th style={{ color: '#cc0000' }}>Usuário</th>
                      <th style={{ color: '#cc0000' }}>Role</th>
                      <th style={{ color: '#cc0000' }}>Criador</th>
                      <th style={{ color: '#cc0000' }}>Limite Consultas</th>
                      <th style={{ color: '#cc0000' }}>Consultas Usadas</th>
                      <th style={{ color: '#cc0000' }}>Consultas Restantes</th>
                      <th style={{ color: '#cc0000' }}>Limite CPF</th>
                      <th style={{ color: '#cc0000' }}>CPF Usadas</th>
                      <th style={{ color: '#cc0000' }}>CPF Restantes</th>
                      <th style={{ color: '#cc0000' }}>Limite CPF TEM CNPJ</th>
                      <th style={{ color: '#cc0000' }}>CPF TEM CNPJ Usadas</th>
                      <th style={{ color: '#cc0000' }}>CPF TEM CNPJ Restantes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {todosLimites.map((user) => (
                      <tr key={user.id}>
                        <td>{user.id}</td>
                        <td>{user.username}</td>
                        <td>
                          <span
                            style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              backgroundColor: getRoleColor(user.role),
                              color: '#fff',
                              fontSize: '0.85rem'
                            }}
                          >
                            {getRoleLabel(user.role)}
                          </span>
                        </td>
                        <td>{user.criador_username || '-'}</td>
                        <td>{user.limite_consultas !== null ? user.limite_consultas : 'Ilimitado'}</td>
                        <td>{user.consultas_usadas || 0}</td>
                        <td>
                          {user.consultas_restantes !== null ? (
                            <span style={{
                              color: user.consultas_restantes === 0 ? '#ff0000' : 
                                     user.consultas_restantes <= (user.limite_consultas || 0) * 0.1 ? '#ff6600' : '#00cc66'
                            }}>
                              {user.consultas_restantes}
                            </span>
                          ) : (
                            'Ilimitado'
                          )}
                        </td>
                        <td>
                          {user.limite_cpf === -1 ? (
                            <span style={{ color: '#ff0000', fontWeight: 'bold' }}>Sem permissão</span>
                          ) : user.limite_cpf !== null ? (
                            user.limite_cpf
                          ) : (
                            'Sem limite'
                          )}
                        </td>
                        <td>{user.consultas_cpf_usadas || 0}</td>
                        <td>
                          {user.consultas_cpf_restantes === -1 ? (
                            <span style={{ color: '#ff0000', fontWeight: 'bold' }}>Sem permissão</span>
                          ) : user.consultas_cpf_restantes !== null ? (
                            <span style={{
                              color: user.consultas_cpf_restantes === 0 ? '#ff0000' : 
                                     user.consultas_cpf_restantes <= (user.limite_cpf || 0) * 0.1 ? '#ff6600' : '#00cc66'
                            }}>
                              {user.consultas_cpf_restantes}
                            </span>
                          ) : (
                            'Sem limite'
                          )}
                        </td>
                        <td>
                          {user.limite_cpf_tem_cnpj === -1 ? (
                            <span style={{ color: '#ff0000', fontWeight: 'bold' }}>Sem permissão</span>
                          ) : user.limite_cpf_tem_cnpj !== null ? (
                            user.limite_cpf_tem_cnpj
                          ) : (
                            'Sem limite'
                          )}
                        </td>
                        <td>{user.consultas_cpf_tem_cnpj_usadas || 0}</td>
                        <td>
                          {user.consultas_cpf_tem_cnpj_restantes === -1 ? (
                            <span style={{ color: '#ff0000', fontWeight: 'bold' }}>Sem permissão</span>
                          ) : user.consultas_cpf_tem_cnpj_restantes !== null ? (
                            <span style={{
                              color: user.consultas_cpf_tem_cnpj_restantes === 0 ? '#ff0000' : 
                                     user.consultas_cpf_tem_cnpj_restantes <= (user.limite_cpf_tem_cnpj || 0) * 0.1 ? '#ff6600' : '#00cc66'
                            }}>
                              {user.consultas_cpf_tem_cnpj_restantes}
                            </span>
                          ) : (
                            'Sem limite'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Seus Limites de Consultas - Apenas Proprietário */}
        {isProprietario && (
          <div className="card" style={{ marginTop: '2rem', backgroundColor: '#0a0a0a' }}>
            <h3 style={{ color: '#cc0000', marginBottom: '1rem' }}>Seus Limites de Consultas</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div style={{ padding: '1rem', backgroundColor: '#1a1a1a', borderRadius: '8px' }}>
                <strong style={{ color: '#999', fontSize: '0.9rem' }}>Consultas (geral)</strong>
                <p style={{ margin: '0.5rem 0', color: '#fff', fontSize: '1.1rem' }}>
                  {limiteConsultasInfo?.limite != null
                    ? `${limiteConsultasInfo?.usadas ?? 0} / ${limiteConsultasInfo?.limite}`
                    : 'Não definido'}
                </p>
                {limiteConsultasInfo?.restantes != null && (
                  <p style={{ margin: 0, color: '#00cc66', fontSize: '0.9rem' }}>Restantes: {limiteConsultasInfo.restantes}</p>
                )}
              </div>
              <div style={{ padding: '1rem', backgroundColor: '#1a1a1a', borderRadius: '8px' }}>
                <strong style={{ color: '#999', fontSize: '0.9rem' }}>CPF</strong>
                <p style={{ margin: '0.5rem 0', color: '#fff', fontSize: '1.1rem' }}>
                  {limiteInfoCpf?.limite != null
                    ? `${limiteInfoCpf?.usadas ?? 0} / ${limiteInfoCpf?.limite}`
                    : 'Não definido'}
                </p>
                {limiteInfoCpf?.restantes != null && (
                  <p style={{ margin: 0, color: '#00cc66', fontSize: '0.9rem' }}>Restantes: {limiteInfoCpf.restantes}</p>
                )}
              </div>
              <div style={{ padding: '1rem', backgroundColor: '#1a1a1a', borderRadius: '8px' }}>
                <strong style={{ color: '#999', fontSize: '0.9rem' }}>CPF tem CNPJ</strong>
                <p style={{ margin: '0.5rem 0', color: '#fff', fontSize: '1.1rem' }}>
                  {limiteInfoCpfTemCnpj?.limite != null
                    ? `${limiteInfoCpfTemCnpj?.usadas ?? 0} / ${limiteInfoCpfTemCnpj?.limite}`
                    : 'Não definido'}
                </p>
                {limiteInfoCpfTemCnpj?.restantes != null && (
                  <p style={{ margin: 0, color: '#00cc66', fontSize: '0.9rem' }}>Restantes: {limiteInfoCpfTemCnpj.restantes}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Visualização de Limites dos Usuários - Apenas Proprietário */}
        {isProprietario && (
          <div className="card" style={{ marginTop: '2rem' }}>
            <h3 style={{ color: '#cc0000', marginBottom: '1rem' }}>Limites dos Meus Usuários</h3>
            {loadingLimites ? (
              <div className="loading">Carregando informações...</div>
            ) : usuariosInfo.length === 0 ? (
              <p style={{ color: '#999' }}>Você ainda não criou nenhum usuário.</p>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th style={{ color: '#cc0000' }}>ID</th>
                      <th style={{ color: '#cc0000' }}>Usuário</th>
                      <th style={{ color: '#cc0000' }}>Limite Consultas</th>
                      <th style={{ color: '#cc0000' }}>Consultas Usadas</th>
                      <th style={{ color: '#cc0000' }}>Consultas Restantes</th>
                      <th style={{ color: '#cc0000' }}>Limite CPF</th>
                      <th style={{ color: '#cc0000' }}>CPF Usadas</th>
                      <th style={{ color: '#cc0000' }}>CPF Restantes</th>
                      <th style={{ color: '#cc0000' }}>Limite CPF tem CNPJ</th>
                      <th style={{ color: '#cc0000' }}>CPF tem CNPJ Usadas</th>
                      <th style={{ color: '#cc0000' }}>CPF tem CNPJ Restantes</th>
                      <th style={{ color: '#cc0000' }}>Operadoras</th>
                      <th style={{ color: '#cc0000' }}>Data de Criação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usuariosInfo.map((usuario) => {
                      const limiteCpf = usuariosLimitesCpf.find(l => l.usuario_id === usuario.id);
                      const limiteCpfTemCnpj = usuariosLimitesCpfTemCnpj.find(l => l.usuario_id === usuario.id);
                      const restantesCpf = limiteCpf != null && limiteCpf.limite_consultas != null
                        ? Math.max(0, (limiteCpf.limite_consultas || 0) - (limiteCpf.usadas || 0))
                        : null;
                      const restantesCpfTemCnpj = limiteCpfTemCnpj != null && limiteCpfTemCnpj.limite_consultas != null
                        ? Math.max(0, (limiteCpfTemCnpj.limite_consultas || 0) - (limiteCpfTemCnpj.usadas || 0))
                        : null;
                      return (
                      <tr key={usuario.id}>
                        <td>{usuario.id}</td>
                        <td>{usuario.username}</td>
                        <td>
                          {usuario.limite_individual !== null ? usuario.limite_individual : 
                           <span style={{ color: '#999', fontStyle: 'italic' }}>Pool</span>}
                        </td>
                        <td>{usuario.consultas_usadas}</td>
                        <td>
                          {usuario.consultas_restantes !== null ? (
                            <span style={{
                              color: usuario.consultas_restantes === 0 ? '#ff0000' : 
                                     usuario.consultas_restantes <= (usuario.limite_individual || 0) * 0.1 ? '#ff6600' : '#00cc66'
                            }}>
                              {usuario.consultas_restantes}
                            </span>
                          ) : (
                            <span style={{ color: '#999', fontStyle: 'italic' }}>Pool</span>
                          )}
                        </td>
                        <td>
                          {limiteCpf?.limite_consultas != null ? limiteCpf.limite_consultas : <span style={{ color: '#999', fontStyle: 'italic' }}>Pool</span>}
                        </td>
                        <td>{limiteCpf ? (limiteCpf.usadas ?? 0) : '-'}</td>
                        <td>{restantesCpf != null ? restantesCpf : <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>}</td>
                        <td>
                          {limiteCpfTemCnpj?.limite_consultas != null ? limiteCpfTemCnpj.limite_consultas : <span style={{ color: '#999', fontStyle: 'italic' }}>Pool</span>}
                        </td>
                        <td>{limiteCpfTemCnpj ? (limiteCpfTemCnpj.usadas ?? 0) : '-'}</td>
                        <td>{restantesCpfTemCnpj != null ? restantesCpfTemCnpj : <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>}</td>
                        <td>
                          {usuario.operadoras_permitidas && usuario.operadoras_permitidas.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                              {usuario.operadoras_permitidas.map(op => (
                                <span key={op} style={{
                                  padding: '0.2rem 0.5rem',
                                  backgroundColor: '#333',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  textTransform: 'uppercase'
                                }}>
                                  {op}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span style={{ color: '#999', fontStyle: 'italic' }}>Todas</span>
                          )}
                        </td>
                        <td>{formatDate(usuario.created_at)}</td>
                      </tr>
                    ); })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Log de Consultas - Apenas Admin */}
        {isAdmin && (
          <div className="card" style={{ marginBottom: '2rem', backgroundColor: '#0a0a0a' }}>
            <h3 style={{ color: '#cc0000', marginBottom: '1rem' }}>Log de Consultas (Últimas 30)</h3>
            
            {loadingLog ? (
              <div className="loading">Carregando log de consultas...</div>
            ) : consultasLog.length === 0 ? (
              <p style={{ color: '#999' }}>Nenhuma consulta registrada ainda.</p>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th style={{ color: '#cc0000' }}>Data/Hora</th>
                      <th style={{ color: '#cc0000' }}>Usuário</th>
                      <th style={{ color: '#cc0000' }}>Operadora</th>
                      <th style={{ color: '#cc0000' }}>CEP</th>
                      <th style={{ color: '#cc0000' }}>Número</th>
                      <th style={{ color: '#cc0000' }}>Fonte</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consultasLog.map((consulta) => (
                      <tr key={consulta.id}>
                        <td>{formatDate(consulta.created_at)}</td>
                        <td>{consulta.username}</td>
                        <td>
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            backgroundColor: '#333',
                            fontSize: '0.85rem',
                            textTransform: 'uppercase'
                          }}>
                            {consulta.operadora}
                          </span>
                        </td>
                        <td>{consulta.cep || '-'}</td>
                        <td>{consulta.numero || '-'}</td>
                        <td>
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            backgroundColor: consulta.fonte === 'API' ? '#cc0000' : '#333',
                            color: consulta.fonte === 'API' ? '#fff' : '#ccc',
                            fontSize: '0.85rem',
                            fontWeight: 'bold'
                          }}>
                            {consulta.fonte || 'Site'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPanel;
